import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Calendar,
  ChevronRight,
  ChevronLeft,
  Clock,
  MapPin,
  Trophy,
  RefreshCw,
  Trash2,
  Shuffle,
  Users2,
  User,
  Users,
  Check,
  CheckCircle,
  AlertCircle,
  Award,
  Shield,
  Zap,
  GitBranch,
  Plus,
  ChevronUp,
  ChevronDown,
  X,
  Edit2,
  Radio,
  Play,
  Info,
  Settings,
  Compass,
} from 'lucide-react';
import { btn } from '../../utils/buttonStyles';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { tournamentAPI, tournamentScheduleAPI, tournamentParticipantAPI, tournamentTeamGroupsAPI } from '../services/tournamentApi';
import { apiCall } from '../../services/apiUtil';
import { claimScorerRole } from '../../scoring/services/claimScorer';
import { generateTournamentSchedule, TOURNAMENT_FORMATS } from '../utils/tournamentScheduleGenerator';
import { generateCustomFormat, assignPlayersToRounds, CUSTOM_FORMAT } from '../utils/customFormatScheduleGenerator';
import { getDoublesCrossGroupMetricsAdvanced } from '../utils/scheduleDefaultsCalculator';
import { getMLPScheduleMetrics, MLP_FORMAT, generateMLPSchedule, MLP_STRUCTURES, assignGroups } from '../utils/mlpScheduler';
import { generateCompassDraw, COMPASS_DRAW_FORMAT } from '../utils/compassDrawScheduler';
import { MatchHandlers } from '../utils/matchHandlers';
import { buildTournamentVO } from '../tournamentVO';
import { isEnabled, isCoOwner, TOURNAMENT_ENTITIES } from '../access';
import GameCard from '../components/GameCard';
import CompassBracketView from '../components/CompassBracketView';
import AddEventModal from '../../events/components/AddEventModal';
import MatchSettingsComponent from '../../scoring/MatchSettingsComponent';
import { getConfig } from '../../config';
import TeamLogo from '../../teams/components/TeamLogo';
import PredictAllButton from '../../predictions/components/PredictAllButton';

/**
 * ScheduleTab Component
 *
 * Fully self-contained tournament schedule management:
 * - Loads and manages schedule data (rounds, matches, standings)
 * - Multi-step wizard for schedule generation
 * - Game management (check-in, start, complete)
 * - Manual schedule editing
 *
 * @param {Object} props
 * @param {Object} props.tournament - Tournament data
 * @param {Array} props.participants - Array of enrolled participants
 * @param {boolean} props.isOrganizer - Whether current user is the tournament organizer
 * @param {Function} props.onOpenLiveTab - Callback to open a live game tab
 * @param {Function} props.onRefreshTournament - Callback to refresh tournament data after status changes
 * @returns {JSX.Element}
 */
export default function ScheduleTab({
  tournament,
  participants = [],
  isOrganizer = false,
  activeTab,
  onOpenLiveTab,
  onRefreshTournament,
}) {
  const { theme } = useTheme();
  const { user, isAuthenticated } = useAuth();

  // API schedule state
  const [apiRounds, setApiRounds] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [generatingSchedule, setGeneratingSchedule] = useState(false);

  // Schedule generation state
  const [scheduleStep, setScheduleStep] = useState('select');
  const [selectedFormat, setSelectedFormat] = useState(null);
  // MLP reuses Custom Format's whole generation engine (round building,
  // group assignment, player rotation) — it's the same underlying schedule
  // problem, just with an always-"parallel" matchup assumption instead of a
  // configurable choice. isCustomLikeFormat covers every place that shared
  // pipeline (UI panel, generate/regenerate, save) applies to both; isMLP is
  // only for the handful of places MLP's behavior actually differs (which
  // defaults calculator to use, hiding the Matchup Style choice).
  const isMLP = selectedFormat?.id === 'mlp';
  // Compass Draw's rounds (group_name/next_round_id/loser_next_round_id
  // already wired by generateCompassDraw) are blindly persisted the same
  // way Custom Format's/MLP's are — same save-path shape, so it shares
  // isCustomLikeFormat rather than getting a fourth parallel branch.
  const isCompassDraw = selectedFormat?.id === 'compass_draw';
  const isCustomLikeFormat = selectedFormat?.id === 'custom' || isMLP || isCompassDraw;
  const [selectedParticipantsForSchedule, setSelectedParticipantsForSchedule] = useState([]);
  const [formatOptions, setFormatOptions] = useState({
    matchesPerPairing: 1,
    teamsToQualify: 4,
    // Custom Format inputs
    playersPerTeam: 2,
    numberOfRounds: 1,
    gamesPerRound: 1,
    numberOfPlayoffGames: 0,
    gamesPerTeam: 1,
    useGrouping: false,
    numberOfGroups: 2,
    advancePerGroup: 2, // Pool Play + Bracket — teams advancing from each group to the knockout stage
    groupMode: 'within',
    groupAssignments: {}, // participantId -> group index (0-based); unset = random
    groupNames: {}, // group index (0-based) -> custom display name; unset = "Group N"
    matchupType: 'parallel', // 'parallel' (MLP-style straight matchup) or 'cross' (every pair plays every pair)
    mlpStructure: 'cross_group', // 'group_playoffs' or 'cross_group' — MLP-only, see mlpScheduler.js MLP_STRUCTURES
    // Single Elimination only — see tournamentScheduleGenerator.js generateSingleElimination
    thirdPlace: false,
    consolation: 'none', // 'none' | 'first_round'
  });
  const [generatedSchedule, setGeneratedSchedule] = useState(null);
  const [scheduleGenerating, setScheduleGenerating] = useState(false);
  const [savingGroups, setSavingGroups] = useState(false);

  // User's team IDs for check-in eligibility and scorer status
  const [userTeamIds, setUserTeamIds] = useState([]);
  const [isScorer, setIsScorer] = useState(false);

  // Start Scoring confirmation modal
  const [pendingStartScoringGame, setPendingStartScoringGame] = useState(null);

  // Team roster modal
  const [teamRosterModal, setTeamRosterModal] = useState(null); // { teamId, teamName, players, loading, error }
  const teamPlayersCache = useRef({});

  // MLP player identity override — session-only (never written to the
  // player's actual profile). Real gender comes from the profile once that's
  // wired through; until then this lets an organizer manually specify which
  // 2 of a team's 4 players are Men's/Women's for a more accurate schedule
  // than the random 2/2 fallback (see splitRosterByGender in mlpScheduler.js).
  const [mlpPlayerGenders, setMlpPlayerGenders] = useState({}); // playerId -> 'male' | 'female'
  const [mlpIdentityModal, setMlpIdentityModal] = useState(null); // { rosters: {teamId: players[]}, loading, error }

  // Per-match settings override modal — { game, useOverride, settings }.
  // Lets an organizer set match rules (e.g. bestOf, maxPoints) for one
  // specific match that differ from the tournament-wide match_configuration;
  // LiveGameTab prefers this over the tournament's settings when present.
  const [matchSettingsModal, setMatchSettingsModal] = useState(null);
  const [savingMatchSettings, setSavingMatchSettings] = useState(false);
  // Team-pairing sub-section of the Match Settings modal — lets an organizer
  // pick which specific players from each already-assigned team play this
  // one match (e.g. which doubles pair), independent of the custom-settings
  // override toggle above it.
  const [pairingRosterA, setPairingRosterA] = useState(null);
  const [pairingRosterB, setPairingRosterB] = useState(null);
  const [loadingPairingRosters, setLoadingPairingRosters] = useState(false);
  const [selectedPairingPlayersA, setSelectedPairingPlayersA] = useState([]);
  const [selectedPairingPlayersB, setSelectedPairingPlayersB] = useState([]);

  // NOTE: tournament comes from GET /tournaments/:id, whose Fastify response
  // schema only whitelists sportTypeId (camelCase) — sport_type_id is stripped.
  // Whether this sport actually supports predictions is checked lazily by
  // PredictAllButton itself, only when clicked — not worth a page-load fetch.
  const predictionSportTypeId = tournament?.sportTypeId ?? tournament?.sport_type_id;
  const predictionSportName = tournament?.sportType?.sportName || tournament?.sport_typea?.sport_name || tournament?.sportName;

  // Same "does this sport/tournament need a per-match player subset" check
  // GameCard uses for its manual "Playing" picker — reused here so Custom
  // Format can auto-assign that subset instead of leaving every match for
  // the organizer to fill in by hand afterward.
  const sportPlayedBy = tournament?.sport_typea?.played_by || tournament?.sportType?.playedBy || null;
  const requiredPlayerCount = tournament?.participantsType === 'doubles' ? 2
    : tournament?.participantsType === 'singles' ? 1
    : null;
  const needsPlayerPicker = sportPlayedBy?.toLowerCase() === 'individual' && requiredPlayerCount != null;

  // Same derivation as GameCard's gameParticipantsType/requiredPlayerCount —
  // a per-match settings override (or a Dreambreaker's fixed singles format)
  // takes priority over the tournament-wide default so the Team Pairing
  // section asks for the right number of players for *this* match, live as
  // the organizer edits the override in the modal.
  const modalGame = matchSettingsModal?.game;
  const modalParticipantsType = matchSettingsModal?.useOverride
    ? matchSettingsModal.settings?.participantsType
    : (modalGame?.game_type === 'dreambreaker' ? 'singles' : tournament?.participantsType);
  const modalRequiredPlayerCount = modalParticipantsType === 'doubles' ? 2
    : modalParticipantsType === 'singles' ? 1
    : null;
  // Only show a side's pairing picker once its roster actually exceeds what
  // the format needs — a 2-player team in a doubles match has nothing to pick.
  const showPairingA = sportPlayedBy?.toLowerCase() === 'individual' && modalRequiredPlayerCount != null
    && modalGame?.participant_a_type === 'team' && Array.isArray(pairingRosterA) && pairingRosterA.length > modalRequiredPlayerCount;
  const showPairingB = sportPlayedBy?.toLowerCase() === 'individual' && modalRequiredPlayerCount != null
    && modalGame?.participant_b_type === 'team' && Array.isArray(pairingRosterB) && pairingRosterB.length > modalRequiredPlayerCount;

  const togglePairingPlayer = (player, selected, setSelected) => {
    const isSelected = selected.some(p => p.id === player.id);
    if (isSelected) {
      setSelected(selected.filter(p => p.id !== player.id));
    } else if (!modalRequiredPlayerCount || selected.length < modalRequiredPlayerCount) {
      setSelected([...selected, player]);
    }
  };

  // Local modal and UI state
  const [editingMatch, setEditingMatch] = useState(null);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [showAddMatchModal, setShowAddMatchModal] = React.useState(false);
  const [selectedRoundForNewMatch, setSelectedRoundForNewMatch] = React.useState(null);
  const [showAddRoundModal, setShowAddRoundModal] = React.useState(false);
  const [showCompleteTournamentDialog, setShowCompleteTournamentDialog] = React.useState(false);
  const [completingTournament, setCompletingTournament] = React.useState(false);
  const [showStartTournamentDialog, setShowStartTournamentDialog] = React.useState(false);
  const [startingTournament, setStartingTournament] = React.useState(false);
  const [showSeedPlayoffsDialog, setShowSeedPlayoffsDialog] = React.useState(false);
  const [seedingPlayoffs, setSeedingPlayoffs] = React.useState(false);
  const [newRoundData, setNewRoundData] = React.useState({ name: '', type: 'round_robin' });
  const [newMatchData, setNewMatchData] = React.useState({ participantA: null, participantB: null });
  const [collapsedRounds, setCollapsedRounds] = useState(new Set());
  const [collapsedMatches, setCollapsedMatches] = useState(new Set());
  const [editingParticipantsForGame, setEditingParticipantsForGame] = useState(null);
  const [leagueSectionCollapsed, setLeagueSectionCollapsed] = useState(false);
  const [playoffSectionCollapsed, setPlayoffSectionCollapsed] = useState(false);
  const [thirdPlaceSectionCollapsed, setThirdPlaceSectionCollapsed] = useState(false);
  const [consolationSectionCollapsed, setConsolationSectionCollapsed] = useState(false);

  // Shared by the "View players" roster modal and auto-assigning players to
  // Custom Format matches — both need the same team -> members lookup, so
  // one cache avoids re-fetching a roster already pulled for the other.
  const getTeamRoster = useCallback(async (teamId) => {
    if (teamPlayersCache.current[teamId]) return teamPlayersCache.current[teamId];
    const teamData = await apiCall(`/teams/${teamId}`);
    const players = (teamData.members || []).map(m => ({
      id: m.user.id,
      name: `${m.user.firstName} ${m.user.lastName}`.trim(),
    }));
    teamPlayersCache.current[teamId] = players;
    return players;
  }, []);

  // Fetches every distinct team roster involved in the schedule being
  // generated, in parallel, so assignPlayersToRounds can auto-pick each
  // match's playing subset without a separate fetch per match. Each team is
  // caught individually — a single team's roster fetch failing (network
  // blip, permissions, whatever) must not silently reject the whole batch
  // and leave every caller (including the MLP eligibility check) stuck with
  // stale/no data.
  const buildRosterByTeamId = useCallback(async (selectedParticipants) => {
    const teamIds = [...new Set(selectedParticipants.filter(p => p.type === 'team').map(p => p.id))];
    const entries = await Promise.all(teamIds.map(async (id) => {
      try {
        return [id, await getTeamRoster(id)];
      } catch (error) {
        console.error(`Failed to fetch roster for team ${id}:`, error);
        return [id, []];
      }
    }));
    return Object.fromEntries(entries);
  }, [getTeamRoster]);

  // Same as buildRosterByTeamId, but stamps in this session's manually-set
  // mlpPlayerGenders overrides — never written back to the player's actual
  // profile, just used to drive splitRosterByGender's real-data path instead
  // of its random fallback for this generation.
  const buildRosterByTeamIdWithGenderOverrides = useCallback(async (selectedParticipants) => {
    const rosterByTeamId = await buildRosterByTeamId(selectedParticipants);
    return Object.fromEntries(
      Object.entries(rosterByTeamId).map(([teamId, roster]) => [
        teamId,
        roster.map(p => mlpPlayerGenders[p.id] ? { ...p, gender: mlpPlayerGenders[p.id] } : p),
      ])
    );
  }, [buildRosterByTeamId, mlpPlayerGenders]);

  // Every team shown in "Assign Teams to Groups" needs a concrete, visible
  // default (no more ambiguous "Random" option) — round-robin balance any
  // team that doesn't already have an explicit assignment, whenever the
  // team list or group count changes. Only fills gaps; never overwrites a
  // choice the organizer already made.
  useEffect(() => {
    if (!isMLP || selectedParticipantsForSchedule.length <= 2) return;
    const numGroups = Number(formatOptions.numberOfGroups) || 2;
    setFormatOptions(prev => {
      const groupAssignments = { ...prev.groupAssignments };
      let changed = false;
      selectedParticipantsForSchedule.forEach((p, idx) => {
        if (groupAssignments[p.id] == null) {
          groupAssignments[p.id] = idx % numGroups;
          changed = true;
        }
      });
      return changed ? { ...prev, groupAssignments } : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMLP, selectedParticipantsForSchedule, formatOptions.numberOfGroups]);

  // Tracks whether every registered team actually has exactly 4 players —
  // MLP requires all teams to field 2 doubles pairs, so a mix like [4, 3]
  // must disable it too, not just an overall max that happens to hit 4.
  // Only relevant for doubles-type sports (requiredPlayerCount === 2) —
  // singles/team sports don't have an MLP concept at all. null while
  // unknown/loading so the dropdown doesn't wrongly disable during a fetch.
  const [teamsHaveExactlyFourPlayers, setTeamsHaveExactlyFourPlayers] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (requiredPlayerCount !== 2 || selectedParticipantsForSchedule.filter(p => p.type === 'team').length === 0) {
      setTeamsHaveExactlyFourPlayers(null);
      return;
    }
    buildRosterByTeamId(selectedParticipantsForSchedule).then(rosterByTeamId => {
      if (cancelled) return;
      const sizes = Object.values(rosterByTeamId).map(r => r.length);
      setTeamsHaveExactlyFourPlayers(sizes.length > 0 && sizes.every(n => n === 4));
    });
    return () => { cancelled = true; };
  }, [selectedParticipantsForSchedule, buildRosterByTeamId, requiredPlayerCount]);

  // Custom Format's smart defaults — playersPerTeam comes from the actual
  // selected teams' real rosters (the max member count among them) rather
  // than the tournament's own players_per_team setting, which is often left
  // unset even when the teams themselves have real rosters (falls back to
  // that tournament setting, then a generic 2, only if no roster data comes
  // back at all).
  //
  // For doubles, the rounds/gamesPerRound split is delegated to
  // getDoublesCrossGroupMetricsAdvanced (MLP-style parallel matchups — see
  // that file for the full reasoning):
  // - teamsPerGroup: from the existing "Split teams into groups" setting
  //   when enabled (numTeams / numberOfGroups); otherwise treats the field
  //   as one faceoff split in half (numTeams / 2 — a plain 2-team tournament
  //   is just teamsPerGroup=1, a single 1v1 faceoff).
  // - capPairsPerTeam reuses the existing "Max Possible Combination / Team"
  //   field (state key: gamesPerTeam) rather than introducing a new input.
  // Team sports (no per-match player subset) keep the simple old fallback:
  // 1 round of however many games can run simultaneously.
  const computeCustomFormatDefaults = useCallback(async () => {
    const numTeams = selectedParticipantsForSchedule.length || 2;
    // MLP rosters are always exactly 4 (2 doubles pairs) — fixed, not
    // derived from whatever the actual registered teams' rosters happen to
    // be, unlike Custom Format.
    let playersPerTeam = 4;
    if (!isMLP) {
      const rosterByTeamId = await buildRosterByTeamId(selectedParticipantsForSchedule);
      const rosterSizes = Object.values(rosterByTeamId).map(r => r.length).filter(n => n > 0);
      playersPerTeam = rosterSizes.length > 0 ? Math.max(...rosterSizes) : (tournament?.playersPerTeam || 2);
    }

    if (requiredPlayerCount !== 2) {
      // Singles or team sports — no partnership concept to build a cross-group split from.
      return { playersPerTeam, gamesPerRound: Math.max(1, Math.floor(numTeams / 2)), numberOfRounds: 1 };
    }

    // MLP's grouping is always active once there are 3+ teams (it's shown
    // unconditionally in the UI, not an optional checkbox like Custom
    // Format's); with exactly 2 teams grouping is moot either way.
    const effectiveUseGrouping = isMLP ? numTeams > 2 : formatOptions.useGrouping;
    const teamsPerGroup = effectiveUseGrouping && formatOptions.numberOfGroups
      ? Math.max(1, Math.round(numTeams / formatOptions.numberOfGroups))
      : Math.max(1, Math.round(numTeams / 2));

    // capPairsPerTeam=null here means "use the full mathematical max" — this
    // is what computes the *default* for the Games per Team field itself
    // (calculatedPairsPerTeam below), not a value read back from it. Reusing
    // whatever Games per Team already held (e.g. the initial hardcoded 1)
    // would collapse the whole schedule to almost nothing, since that field
    // hasn't been set to anything meaningful yet at this point.
    const { calculatedPairsPerTeam, gamesPerRound, totalRounds } = isMLP
      ? getMLPScheduleMetrics(teamsPerGroup, playersPerTeam, 1, formatOptions.mlpStructure, formatOptions.numberOfGroups)
      : getDoublesCrossGroupMetricsAdvanced(
          teamsPerGroup, playersPerTeam, 1, null,
          // Matchup Style is only ever shown/editable when grouping is on
          // (the dropdown is hidden otherwise) — without explicit grouping
          // there's no "MLP-style parallel" structure to speak of, so it
          // should always fall back to 'cross' rather than silently reusing
          // whatever's left in state (which could be stale from a previous
          // grouped configuration).
          formatOptions.useGrouping ? formatOptions.matchupType : 'cross'
        );
    return { playersPerTeam, gamesPerRound, numberOfRounds: totalRounds, gamesPerTeam: calculatedPairsPerTeam };
  }, [selectedParticipantsForSchedule, buildRosterByTeamId, tournament?.playersPerTeam, requiredPlayerCount, formatOptions.useGrouping, formatOptions.numberOfGroups, formatOptions.matchupType, formatOptions.mlpStructure, isMLP]);

  // Applies Custom Format's smart defaults whenever it's the selected format
  // for a given team selection *and* matchup configuration — a plain
  // onChange on the format dropdown isn't enough, since it only fires on an
  // actual value change and not when Custom Format was already selected
  // (e.g. navigating back to Select Teams, changing the roster, then
  // returning to this step). The signature includes grouping/matchup-type
  // settings too, not just team IDs — Number of Rounds/Games per Round/Games
  // per Team are computed *from* those settings, so flipping e.g. Matchup
  // Style from straight to cross without recomputing them would leave a
  // schedule sized for the old setting, producing a mismatched/wrong
  // schedule for the new one. Tracks the full signature so it only recomputes
  // (and doesn't re-clobber a manual edit) when something it depends on
  // actually changes.
  const customDefaultsAppliedFor = useRef(null);
  useEffect(() => {
    // Custom Format no longer auto-computes anything — the organizer
    // directly provides Number of Rounds/Games per Round/Playoff Games and
    // that's exactly what gets generated. Only MLP still needs this (it has
    // no manual overrides left at all).
    if (!isMLP) return;
    const teamIds = selectedParticipantsForSchedule.map(p => p.id).sort().join(',');
    const signature = [
      selectedFormat?.id,
      teamIds,
      formatOptions.useGrouping,
      formatOptions.numberOfGroups,
      formatOptions.groupMode,
      formatOptions.matchupType,
      formatOptions.mlpStructure,
    ].join('|');
    if (customDefaultsAppliedFor.current === signature) return;
    customDefaultsAppliedFor.current = signature;

    let cancelled = false;
    computeCustomFormatDefaults().then(defaults => {
      if (!cancelled) setFormatOptions(prev => ({ ...prev, ...defaults }));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFormat?.id, selectedParticipantsForSchedule, formatOptions.useGrouping, formatOptions.numberOfGroups, formatOptions.groupMode, formatOptions.matchupType, formatOptions.mlpStructure]);

  // Live, always-visible readout of getDoublesCrossGroupMetricsAdvanced's raw
  // output for whatever is *currently typed* in the form — purely
  // synchronous (reads formatOptions directly, no roster fetch), so it
  // updates instantly on every keystroke/toggle for transparency, separate
  // from the smart-defaults auto-fill above. Games per Team is read as the
  // real capPairsPerTeam input here (not hardcoded to the uncapped max) —
  // it's a load-bearing field, not decorative. Exposes both the exact input
  // object and the result so the UI can show precisely what was sent in.
  const liveScheduleCalcInput = useMemo(() => {
    if (requiredPlayerCount !== 2) return null;
    const numTeams = selectedParticipantsForSchedule.length || 2;
    // MLP's grouping is always active once there are 3+ teams (it's shown
    // unconditionally in the UI, not an optional checkbox like Custom
    // Format's); with exactly 2 teams grouping is moot either way.
    const effectiveUseGrouping = isMLP ? numTeams > 2 : formatOptions.useGrouping;
    const teamsPerGroup = effectiveUseGrouping && formatOptions.numberOfGroups
      ? Math.max(1, Math.round(numTeams / formatOptions.numberOfGroups))
      : Math.max(1, Math.round(numTeams / 2));
    return {
      teamsPerGroup,
      // MLP rosters are always exactly 4 — force it here too rather than
      // relying solely on the async smart-defaults effect having already run.
      playersPerTeam: isMLP ? 4 : (Number(formatOptions.playersPerTeam) || 2),
      y: 1,
      // MLP always uses the full uncapped combination count — the field is
      // hidden for it, so never read a possibly-stale formatOptions.gamesPerTeam.
      capPairsPerTeam: isMLP ? null : (Number(formatOptions.gamesPerTeam) || null),
      // MLP has no matchup-style choice (always parallel) — this is only
      // meaningful, and only shown/editable, for Custom Format with grouping
      // on; otherwise it must default to 'cross' rather than reusing a stale
      // 'parallel' left over in state.
      ...(isMLP
        ? { mlpStructure: formatOptions.mlpStructure, numberOfGroups: formatOptions.numberOfGroups }
        : { matchupType: effectiveUseGrouping ? formatOptions.matchupType : 'cross' }),
    };
  }, [requiredPlayerCount, selectedParticipantsForSchedule, formatOptions.playersPerTeam, formatOptions.gamesPerTeam, formatOptions.useGrouping, formatOptions.numberOfGroups, formatOptions.matchupType, formatOptions.mlpStructure, isMLP]);

  const liveScheduleCalc = useMemo(() => {
    if (!liveScheduleCalcInput) return null;
    const { teamsPerGroup, playersPerTeam, y, capPairsPerTeam, matchupType, mlpStructure, numberOfGroups } = liveScheduleCalcInput;
    return isMLP
      ? getMLPScheduleMetrics(teamsPerGroup, playersPerTeam, y, mlpStructure, numberOfGroups)
      : getDoublesCrossGroupMetricsAdvanced(teamsPerGroup, playersPerTeam, y, capPairsPerTeam, matchupType);
  }, [liveScheduleCalcInput, isMLP]);

  const handleShowTeamRoster = useCallback(async (e, teamId, teamName) => {
    e.stopPropagation();
    if (teamPlayersCache.current[teamId]) {
      setTeamRosterModal({ teamId, teamName, players: teamPlayersCache.current[teamId], loading: false });
      return;
    }
    setTeamRosterModal({ teamId, teamName, players: [], loading: true });
    try {
      const players = await getTeamRoster(teamId);
      setTeamRosterModal({ teamId, teamName, players, loading: false });
    } catch {
      setTeamRosterModal({ teamId, teamName, players: [], loading: false, error: true });
    }
  }, []);

  const handleOpenMlpIdentityModal = useCallback(async () => {
    setMlpIdentityModal({ rosters: {}, loading: true, error: false });
    try {
      const rosterByTeamId = await buildRosterByTeamId(selectedParticipantsForSchedule);
      setMlpIdentityModal({ rosters: rosterByTeamId, loading: false, error: false });
    } catch {
      setMlpIdentityModal({ rosters: {}, loading: false, error: true });
    }
  }, [buildRosterByTeamId, selectedParticipantsForSchedule]);

  // A team's identities are "complete" once its roster (up to 4 players) has
  // exactly 2 marked male and 2 marked female — that's what MLP actually
  // needs (Men's/Women's Doubles + 2 mixed pairs), a lopsided or partial
  // split isn't enough to safely stop randomizing for that team.
  const mlpIdentityIsCompleteForTeam = useCallback((roster) => {
    const relevant = (roster || []).slice(0, 4);
    if (relevant.length === 0) return false;
    const males = relevant.filter(p => mlpPlayerGenders[p.id] === 'male').length;
    const females = relevant.filter(p => mlpPlayerGenders[p.id] === 'female').length;
    return males === 2 && females === 2;
  }, [mlpPlayerGenders]);

  // Shared by the "Regenerate" button and the identity modal's "Apply &
  // Regenerate" — same regeneration logic either way, the only difference
  // is whether mlpPlayerGenders has just been updated beforehand.
  const regenerateSchedule = useCallback(async () => {
    setScheduleGenerating(true);
    try {
      let schedule;
      if (isMLP) {
        const rosterByTeamId = await buildRosterByTeamIdWithGenderOverrides(selectedParticipantsForSchedule);
        schedule = generateMLPSchedule(selectedParticipantsForSchedule, rosterByTeamId, {
          useGrouping: selectedParticipantsForSchedule.length > 2,
          numberOfGroups: formatOptions.numberOfGroups,
          groupAssignments: formatOptions.groupAssignments,
          structure: formatOptions.mlpStructure,
          numberOfPlayoffGames: formatOptions.numberOfPlayoffGames,
        });
      } else if (selectedFormat.id === 'custom') {
        schedule = generateCustomFormat(
          selectedParticipantsForSchedule,
          { ...formatOptions, requiredPlayerCount }
        );
        if (needsPlayerPicker) {
          const rosterByTeamId = await buildRosterByTeamId(selectedParticipantsForSchedule);
          schedule = { ...schedule, rounds: assignPlayersToRounds(schedule.rounds, rosterByTeamId, requiredPlayerCount) };
        }
      } else if (isCompassDraw) {
        schedule = generateCompassDraw(selectedParticipantsForSchedule);
      } else {
        schedule = generateTournamentSchedule(
          selectedParticipantsForSchedule,
          selectedFormat.id,
          { ...formatOptions }
        );
      }
      setGeneratedSchedule(schedule);
      toast.success('Schedule regenerated with new random matchups!');
      // Stable id (content-based) so regenerating with the same structural
      // warning (e.g. "5 teams, at most 2 games per round") just refreshes
      // the existing toast instead of stacking a new identical one each time.
      schedule.warnings?.forEach(w => toast(w, { id: `schedule-warning-${w}`, icon: '⚠️', duration: 6000 }));
    } catch (error) {
      toast.error(error.message || 'Failed to regenerate');
    } finally {
      setScheduleGenerating(false);
    }
  }, [isMLP, isCompassDraw, selectedFormat, selectedParticipantsForSchedule, formatOptions, requiredPlayerCount, needsPlayerPicker, buildRosterByTeamIdWithGenderOverrides, buildRosterByTeamId]);

  // Build Tournament VO for access control
  const tournamentVO = useMemo(() => {
    if (!tournament) return null;
    return buildTournamentVO(tournament, apiRounds, participants, user, {});
  }, [tournament, apiRounds, participants, user]);

  // Load schedule from API (rounds contain games). This tab is never
  // actually unmounted when switching away from it — TournamentDetailPage
  // just hides it with display:none — so a plain [tournament?.id] dependency
  // only ever fires once on first mount. Also refetch whenever activeTab
  // switches back to 'schedule' (e.g. returning from live scoring after
  // completing a game), so the data doesn't stay stale until a full page
  // reload. Skips refetching while hidden to avoid wasted calls; the
  // `activeTab !== undefined` check keeps this backward-compatible with any
  // other place ScheduleTab might be rendered without that prop.
  useEffect(() => {
    if (activeTab !== undefined && activeTab !== 'schedule') return;
    const loadSchedule = async () => {
      if (!tournament?.id) return;
      setScheduleLoading(true);
      try {
        const rounds = await tournamentScheduleAPI.getRounds(tournament.id);
        setApiRounds(rounds || []);
      } catch (error) {
        console.error('Failed to load schedule:', error);
      } finally {
        setScheduleLoading(false);
      }
    };
    loadSchedule();
  }, [tournament?.id, activeTab]);

  // Set default matchesPerPairing for IPL format
  useEffect(() => {
    if (selectedFormat?.id === 'ipl_style') {
      setFormatOptions(prev => ({
        ...prev,
        matchesPerPairing: prev.matchesPerPairing || 2, // Default to double round-robin for IPL
      }));
    }
  }, [selectedFormat]);

  // Auto-collapse completed rounds and league section
  useEffect(() => {
    if (!apiRounds || apiRounds.length === 0) return;

    const completedRoundKeys = apiRounds
      .filter(round => {
        // Check if all games in the round are completed
        if (!round.games || round.games.length === 0) return false;
        return round.games.every(game =>
          game.status === 'completed' ||
          game.status === 'Completed' ||
          game.checkin_status === 'completed'
        );
      })
      .map(round => round.roundKey);

    // Auto-collapse league section if all league matches are completed
    const leagueRounds = apiRounds.filter(r => r.round_type === 'round_robin');
    const allLeagueMatchesCompleted = leagueRounds.length > 0 && leagueRounds.every(round =>
      round.games?.every(game =>
        game.status === 'completed' || game.status === 'Completed' || game.checkin_status === 'completed'
      )
    );
    if (allLeagueMatchesCompleted) {
      setLeagueSectionCollapsed(true);
    }

    if (completedRoundKeys.length > 0) {
      setCollapsedRounds(new Set(completedRoundKeys));
    }
  }, [apiRounds]);

  // For each saved Dreambreaker game, works out whether it's actually needed:
  // real MLP only plays it if the other 4 games in that same team-pairing's
  // series are tied 2-2; if one team already won 3, it's not needed. Keyed
  // off the saved data itself (game_type), not in-session format selection —
  // isMLP alone wouldn't be set if the user navigates straight to an
  // already-saved schedule's Matches tab without re-visiting Choose Format.
  //
  // Tallies whatever of the 4 games are *already* completed — doesn't wait
  // for all 4. "Not needed" can be known the moment either side reaches 3
  // wins, even with a game still pending (the last result can't undo a 3rd
  // win). "Required" can only be confirmed once all 4 are actually in and
  // tied 2-2 — a 2-1 with one game left is still genuinely undetermined.
  const mlpDreambreakerStatusByGameId = useMemo(() => {
    const allGames = apiRounds.flatMap(r => r.games || []);
    const dreambreakers = allGames.filter(g => g.game_type === 'dreambreaker');
    const statusByGameId = {};
    dreambreakers.forEach(db => {
      const seriesGames = allGames.filter(g =>
        g.id !== db.id &&
        g.game_type && g.game_type !== 'dreambreaker' &&
        ((g.participant_a_id === db.participant_a_id && g.participant_b_id === db.participant_b_id) ||
         (g.participant_a_id === db.participant_b_id && g.participant_b_id === db.participant_a_id))
      );
      if (seriesGames.length === 0) return;

      const completedSeriesGames = seriesGames.filter(g =>
        g.status === 'completed' || g.status === 'Completed' || g.checkin_status === 'completed'
      );

      // Tally wins in terms of the Dreambreaker match's own A/B orientation —
      // other games in the series may have A/B flipped relative to it.
      let aWins = 0, bWins = 0;
      completedSeriesGames.forEach(g => {
        const aScore = Number(g.participant_a_score);
        const bScore = Number(g.participant_b_score);
        if (!Number.isFinite(aScore) || !Number.isFinite(bScore) || aScore === bScore) return;
        const gameASideMatchesDbASide = g.participant_a_id === db.participant_a_id;
        const aWonThisGame = aScore > bScore;
        if (aWonThisGame === gameASideMatchesDbASide) aWins++; else bWins++;
      });

      if (aWins >= 3 || bWins >= 3) {
        // Decided already — no remaining result can bring it back to 2-2.
        statusByGameId[db.id] = { ready: true, required: false, aWins, bWins };
      } else if (completedSeriesGames.length === seriesGames.length && aWins === 2 && bWins === 2) {
        statusByGameId[db.id] = { ready: true, required: true, aWins, bWins };
      } else {
        statusByGameId[db.id] = { ready: false };
      }
    });
    return statusByGameId;
  }, [apiRounds]);

  // Check user's team IDs and scorer status for game management
  useEffect(() => {
    const checkUserPermissions = async () => {
      if (!tournament?.id || !user?.id || !isAuthenticated) return;

      try {
        // Check if user has any registrations for this tournament
        const existingRegistration = await tournamentParticipantAPI.checkRegistration(tournament.id, user.id, []);
        const registrationData = existingRegistration?.data || existingRegistration || [];

        if (registrationData.length > 0) {
          // Set scorer status
          const hasScorePermission = registrationData.some(reg => reg.scorer);
          setIsScorer(hasScorePermission);

          // Set user's team IDs for check-in eligibility
          const teamRegistrations = registrationData.filter(reg => reg.teamId && reg.teamId > 0);
          const teamIds = teamRegistrations.map(reg => reg.teamId).filter(Boolean);
          setUserTeamIds(teamIds);
        }
      } catch (error) {
        console.error('Failed to check user permissions:', error);
      }
    };

    checkUserPermissions();
  }, [tournament?.id, user?.id, isAuthenticated]);

  // Create MatchHandlers instance for game operations
  const matchHandlers = new MatchHandlers({ user });

  // Handler for opening a live game tab
  const handleOpenLiveTab = async (game, gameCommonId = null) => {
    try {
      const teamsData = await matchHandlers.buildTeamsDataFromGame(game);

      const label = `${game.participant_a_name || 'Team A'} vs ${game.participant_b_name || 'Team B'}`;
      const shortLabel = label.length > 20 ? label.substring(0, 17) + '...' : label;

      const gameTab = {
        gameId: game.id,
        gameCommonId,
        label: shortLabel,
        sportName: tournament?.sportType?.sportName || tournament?.sport_typea?.sport_name || tournament?.sportName,
        teamsData: teamsData
      };

      if (onOpenLiveTab) {
        onOpenLiveTab(gameTab);
      }
    } catch (error) {
      console.error('[ScheduleTab] Failed to open live tab:', error);
      toast.error('Failed to open live game: ' + error.message);
    }
  };

  // Handler for scoring confirmation: start game, create game_common, claim scorer, open live tab
  const handleStartScoring = async (game) => {
    setPendingStartScoringGame(null);

    // Transition tournament round to in_progress
    try {
      await tournamentScheduleAPI.startGame(game.id);
      const rounds = await tournamentScheduleAPI.getRounds(tournament.id);
      setApiRounds(rounds || []);
    } catch (err) {
      console.warn('[ScheduleTab] startGame failed, proceeding anyway:', err);
    }

    // Create game_common and claim scorer — mirrors CurrentEventTab.handleConfirmStartScoring
    let gameCommonId = null;
    const eventId = game.event?.id;
    const sportTypeId = game.tournament?.sport_type_id || tournament?.sport_type_id;

    if (eventId && sportTypeId) {
      try {
        // Create game_common with setup status
        const gameData = await apiCall('/games', {
          method: 'POST',
          body: JSON.stringify({
            eventId,
            sportTypeId,
            tournamentRoundId: game.id,
            status: 'setup',
          }),
        });
        gameCommonId = gameData.id;

        // Claim scorer — locks this user as the sole scorer.
        //
        // Proceeding either way is deliberate here: this path does not pass
        // `isScorer` down, so GameComponent's own polling works out who holds
        // the role and drops this user to view-only if it is somebody else.
        // The server refuses their writes regardless.
        await claimScorerRole(gameCommonId);
      } catch (err) {
        console.warn('[ScheduleTab] Failed to create game_common:', err);
      }
    }

    handleOpenLiveTab(game, gameCommonId);
  };

  // Handle opening schedule modal for a match
  const handleEditMatch = (match) => {
    // Prepare initial data from match event if available
    let initialData = null;

    if (match?.event) {
      const event = match.event;

      // Extract date and time from timestamps
      let date = '';
      let startTime = '';
      let endTime = '';

      if (event.start_time) {
        const startDate = new Date(event.start_time);
        date = startDate.toISOString().split('T')[0];
        startTime = startDate.toTimeString().slice(0, 5);
      }

      if (event.end_time) {
        const endDate = new Date(event.end_time);
        endTime = endDate.toTimeString().slice(0, 5);
      }

      initialData = {
        subject: event.subject || `${match.participant_a_name || 'TBD'} vs ${match.participant_b_name || 'TBD'}`,
        address: event.venue?.fullAddress || '',
        locationName: event.venue?.name || '',
        date: date || new Date().toISOString().split('T')[0],
        startTime: startTime || '09:00',
        endTime: endTime || '10:00'
      };
    } else {
      // No event yet, use tournament data as defaults
      let defaultDate = new Date().toISOString().split('T')[0];
      let defaultStartTime = '09:00';
      let defaultEndTime = '10:00';
      let defaultAddress = '';

      // Extract tournament start date and time if available
      if (tournament?.startDate) {
        const tournamentStart = new Date(tournament.startDate);
        defaultDate = tournamentStart.toISOString().split('T')[0];
        // Use tournament's actual time (e.g., 11 PM instead of 9 AM)
        defaultStartTime = tournamentStart.toTimeString().slice(0, 5);

        // Set end time to 1 hour after start time
        const endTimeDate = new Date(tournamentStart);
        endTimeDate.setHours(endTimeDate.getHours() + 1);
        defaultEndTime = endTimeDate.toTimeString().slice(0, 5);
      }

      // Extract facility address if available
      if (tournament?.facility?.addresses?.formatted_address) {
        defaultAddress = tournament.facility.addresses.formatted_address;
      } else if (tournament?.facility?.facilityName) {
        defaultAddress = tournament.facility.facilityName;
      }

      initialData = {
        subject: `${match.participant_a_name || 'TBD'} vs ${match.participant_b_name || 'TBD'}`,
        address: defaultAddress,
        date: defaultDate,
        startTime: defaultStartTime,
        endTime: defaultEndTime
      };
    }

    setEditingMatch({ ...match, initialData });
    setShowEditModal(true);
  };

  // Handle starting the tournament
  const handleStartTournament = async () => {
    try {
      setStartingTournament(true);

      // Update tournament status to in_progress
      await tournamentAPI.update(tournament.id, {
        status: 'in_progress'
      });

      toast.success('Tournament started! Registration is now locked.');

      // Refresh the page to update the tournament status
      window.location.reload();
    } catch (error) {
      console.error('Error starting tournament:', error);
      toast.error(error.message || 'Failed to start tournament');
    } finally {
      setStartingTournament(false);
    }
  };

  // Handle seeding playoffs — called after the user confirms in the dialog
  const handleSeedPlayoffs = async () => {
    try {
      setSeedingPlayoffs(true);
      const result = await tournamentScheduleAPI.seedPlayoffs(tournament.id);
      toast.success(result.message || 'Playoffs seeded successfully!');
      const rounds = await tournamentScheduleAPI.getRounds(tournament.id);
      setApiRounds(rounds || []);
      setShowSeedPlayoffsDialog(false);
    } catch (error) {
      console.error('Failed to seed playoffs:', error);
      toast.error(error.message || 'Failed to seed playoffs');
    } finally {
      setSeedingPlayoffs(false);
    }
  };

  // Opens the per-match settings override modal. Starting point for the
  // fields is the match's existing override if it has one, otherwise the
  // tournament's own match settings (so the form starts from something
  // sensible rather than blank defaults) — either way, nothing is actually
  // saved as an override until the user explicitly turns it on and saves.
  // Tournament objects come back from the API with this already parsed and
  // camelCased as `matchConfiguration` (see transformResponse in the
  // backend's tournaments.js) — the raw `match_configuration` field isn't
  // what's actually present on this prop.
  const handleOpenMatchSettings = async (game) => {
    const parsedMatchConfig = tournament?.matchConfiguration && typeof tournament.matchConfiguration === 'object'
      ? tournament.matchConfiguration
      : {};
    // tournament.participantsType (top-level) is the real source of truth
    // for this field, same precedence LiveGameTab.jsx uses — it overrides
    // whatever's (or isn't) stored inside matchConfiguration itself.
    const tournamentSettings = tournament?.participantsType
      ? { ...parsedMatchConfig, participantsType: tournament.participantsType }
      : parsedMatchConfig;
    const hasExistingOverride = !!(game.overridden_match_settings && Object.keys(game.overridden_match_settings).length > 0);
    setMatchSettingsModal({
      game,
      useOverride: hasExistingOverride,
      settings: hasExistingOverride ? { ...game.overridden_match_settings } : tournamentSettings,
    });

    // Populate the Team Pairing section from whatever's already saved for
    // this match, defaulting to empty (nothing pre-picked) otherwise.
    setSelectedPairingPlayersA(game.participant_a_players || []);
    setSelectedPairingPlayersB(game.participant_b_players || []);
    setPairingRosterA(null);
    setPairingRosterB(null);
    if (game.participant_a_type === 'team' || game.participant_b_type === 'team') {
      setLoadingPairingRosters(true);
      try {
        const [rosterA, rosterB] = await Promise.all([
          game.participant_a_type === 'team' ? getTeamRoster(game.participant_a_id) : Promise.resolve(null),
          game.participant_b_type === 'team' ? getTeamRoster(game.participant_b_id) : Promise.resolve(null),
        ]);
        setPairingRosterA(rosterA);
        setPairingRosterB(rosterB);
      } catch (error) {
        console.error('Failed to load team rosters for pairing:', error);
      } finally {
        setLoadingPairingRosters(false);
      }
    }
  };

  const handleSaveMatchSettings = async () => {
    if (!matchSettingsModal) return;
    if (showPairingA && selectedPairingPlayersA.length !== modalRequiredPlayerCount) {
      toast.error(`Select exactly ${modalRequiredPlayerCount} player${modalRequiredPlayerCount === 1 ? '' : 's'} for ${matchSettingsModal.game.participant_a_name}`);
      return;
    }
    if (showPairingB && selectedPairingPlayersB.length !== modalRequiredPlayerCount) {
      toast.error(`Select exactly ${modalRequiredPlayerCount} player${modalRequiredPlayerCount === 1 ? '' : 's'} for ${matchSettingsModal.game.participant_b_name}`);
      return;
    }
    setSavingMatchSettings(true);
    try {
      const { game, useOverride, settings } = matchSettingsModal;
      await tournamentScheduleAPI.updateGame(game.id, {
        overridden_match_settings: useOverride ? settings : null,
        ...(showPairingA ? { participant_a_players: selectedPairingPlayersA } : {}),
        ...(showPairingB ? { participant_b_players: selectedPairingPlayersB } : {}),
      });
      toast.success(useOverride ? 'Match settings override saved' : 'Match settings override cleared');
      setMatchSettingsModal(null);
      const rounds = await tournamentScheduleAPI.getRounds(tournament.id);
      setApiRounds(rounds || []);
    } catch (error) {
      console.error('Failed to save match settings override:', error);
      toast.error(error.message || 'Failed to save match settings');
    } finally {
      setSavingMatchSettings(false);
    }
  };

  // Handle saving match schedule updates
  const handleSaveMatch = async (eventData) => {
    try {
      if (!editingMatch) return;


      // Convert eventData format to API format
      // AddEventModal provides: { subject, address, date, startTime, endTime }
      // API expects: { start_time, end_time, address } as ISO timestamps

      const startDateTime = new Date(`${eventData.date}T${eventData.startTime}`).toISOString();
      const endDateTime = new Date(`${eventData.date}T${eventData.endTime}`).toISOString();

      const updateData = {
        start_time: startDateTime,
        end_time: endDateTime,
        address: eventData.address || 'TBD'
      };

      await tournamentScheduleAPI.updateGame(editingMatch.id, updateData);

      // Refresh rounds to show updated schedule in real-time
      const rounds = await tournamentScheduleAPI.getRounds(tournament.id);
      setApiRounds(rounds);

      toast.success('Match scheduled successfully');

      // Note: Modal will close automatically via AddEventModal's onClose
    } catch (error) {
      console.error('Error saving match schedule:', error);
      toast.error(error.message || 'Failed to schedule match');
      throw error;
    }
  };

  // Handle adding a new round
  const handleAddRound = async () => {
    try {
      if (!newRoundData.name || !newRoundData.type) {
        toast.error('Please fill in all required fields');
        return;
      }

      // Always the next sequential number — a manual override was never
      // needed in practice (it just defaulted to this anyway) and only
      // added a way to accidentally collide with an existing round number.
      const roundNumber = apiRounds.length + 1;

      await tournamentScheduleAPI.createRound(tournament.id, {
        round_name: newRoundData.name,
        round_type: newRoundData.type,
        round_number: roundNumber,
      });

      toast.success('Round created successfully');

      // Refresh rounds
      const rounds = await tournamentScheduleAPI.getRounds(tournament.id);
      setApiRounds(rounds);

      // Reset and close modal
      setShowAddRoundModal(false);
      setNewRoundData({ name: '', type: 'round_robin' });
    } catch (error) {
      console.error('Error creating round:', error);
      toast.error(error.message || 'Failed to create round');
    }
  };

  // Handle deleting a round
  const handleDeleteRound = async (roundNumber) => {
    if (!window.confirm(`Are you sure you want to delete round ${roundNumber}? This will remove all matches in this round.`)) {
      return;
    }

    try {
      await tournamentScheduleAPI.deleteRound(tournament.id, roundNumber);
      toast.success('Round deleted successfully');

      // Refresh rounds
      const rounds = await tournamentScheduleAPI.getRounds(tournament.id);
      setApiRounds(rounds);
    } catch (error) {
      console.error('Error deleting round:', error);
      toast.error(error.message || 'Failed to delete round');
    }
  };

  // Handle adding a new match to a round
  const handleAddMatch = async () => {
    try {
      if (!selectedRoundForNewMatch || !newMatchData.participantA || !newMatchData.participantB) {
        toast.error('Please select both participants');
        return;
      }
      if (newMatchData.participantA === newMatchData.participantB) {
        toast.error('Participant A and B cannot be the same team');
        return;
      }

      const [typeA, idA] = newMatchData.participantA.split(':');
      const [typeB, idB] = newMatchData.participantB.split(':');

      await tournamentScheduleAPI.createMatch(tournament.id, {
        round_number: selectedRoundForNewMatch,
        participant_a_type: typeA,
        participant_a_id: parseInt(idA),
        participant_b_type: typeB,
        participant_b_id: parseInt(idB),
      });

      toast.success('Match created successfully');

      // Refresh rounds
      const rounds = await tournamentScheduleAPI.getRounds(tournament.id);
      setApiRounds(rounds);

      // Reset and close modal
      setShowAddMatchModal(false);
      setSelectedRoundForNewMatch(null);
      setNewMatchData({ participantA: null, participantB: null });
    } catch (error) {
      console.error('Error creating match:', error);
      toast.error(error.message || 'Failed to create match');
    }
  };

  // Handle deleting a match
  const handleDeleteMatch = async (matchId) => {
    if (!window.confirm('Are you sure you want to delete this match?')) {
      return;
    }

    try {
      await tournamentScheduleAPI.deleteMatch(matchId);
      toast.success('Match deleted successfully');

      // Refresh rounds
      const rounds = await tournamentScheduleAPI.getRounds(tournament.id);
      setApiRounds(rounds);
    } catch (error) {
      console.error('Error deleting match:', error);
      toast.error(error.message || 'Failed to delete match');
    }
  };

  // Handle moving a match up or down within a round
  const handleMoveMatch = async (match, direction) => {
    try {
      // group_name too, not just round_number — group_knockout pools each
      // restart round_number at 1, so round_number alone can match the wrong
      // group's round of the same number.
      const currentRound = apiRounds.find(r => r.round_number === match.round_number && r.group_name === match.group_name);
      if (!currentRound || !currentRound.games) return;

      const currentIndex = currentRound.games.findIndex(g => g.id === match.id);
      if (currentIndex === -1) return;

      // Determine new position
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      if (newIndex < 0 || newIndex >= currentRound.games.length) {
        toast.error('Cannot move match further in this direction');
        return;
      }

      const targetMatch = currentRound.games[newIndex];

      // Two parallel PATCHes here would race against the DB's unique
      // (tournament_id, round_number, match_number) constraint — whichever
      // update ran first would try to claim a match_number the other match
      // still held. swapMatchOrder does this atomically server-side instead.
      await tournamentScheduleAPI.swapMatchOrder(match.id, targetMatch.id);

      toast.success('Match reordered successfully');

      // Refresh rounds
      const rounds = await tournamentScheduleAPI.getRounds(tournament.id);
      setApiRounds(rounds);
    } catch (error) {
      console.error('Error reordering match:', error);
      toast.error(error.message || 'Failed to reorder match');
    }
  };

  // Handle completing the tournament
  const handleCompleteTournament = async () => {
    setCompletingTournament(true);
    try {
      await tournamentAPI.update(tournament.id, { status: 'completed' });
      toast.success('Tournament completed successfully!');
      setShowCompleteTournamentDialog(false);

      // Refresh tournament data from parent
      window.location.reload(); // Simple refresh for now
    } catch (error) {
      console.error('Error completing tournament:', error);
      toast.error(error.message || 'Failed to complete tournament');
    } finally {
      setCompletingTournament(false);
    }
  };

  // Toggle round collapse state — keyed by roundKey (round_number is not
  // unique across groups in group_knockout pools), see groupGamesByRound.
  const toggleRoundCollapse = (roundKey) => {
    setCollapsedRounds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(roundKey)) {
        newSet.delete(roundKey);
      } else {
        newSet.add(roundKey);
      }
      return newSet;
    });
  };

  // Toggle match collapse state
  const toggleMatchCollapse = (matchId) => {
    setCollapsedMatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(matchId)) {
        newSet.delete(matchId);
      } else {
        newSet.add(matchId);
      }
      return newSet;
    });
  };

  // Handler for check-in success - refresh games
  const handleCheckInSuccess = async (gameId, result) => {
    // Update the game in apiRounds with new checkin data
    setApiRounds(prevRounds => prevRounds.map(round => ({
      ...round,
      games: round.games.map(game =>
        game.id === gameId
          ? { ...game, checkins: result.checkins, checkin_status: result.checkin_status }
          : game
      )
    })));
  };

  // Handler for game start
  const handleGameStart = async (gameId, result) => {

    // Refresh schedule to get the updated game status
    const rounds = await tournamentScheduleAPI.getRounds(tournament.id);
    setApiRounds(rounds || []);

    toast.success('Match started! Click "Live" to begin scoring.');
  };

  return (
    <div className="space-y-6">
      {/* Schedule Header with Progress Steps */}
      <div className={`${theme.backgroundSecondary} rounded-lg p-4`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-bold ${theme.text} flex items-center`}>
            <Calendar className={`w-5 h-5 mr-2 ${theme.link}`} />
            Tournament Schedule
          </h3>
          {generatedSchedule && (
            <button
              onClick={() => {
                setGeneratedSchedule(null);
                setScheduleStep('select');
                setSelectedFormat(null);
                setSelectedParticipantsForSchedule([]);
              }}
              className={`${btn.base} ${btn.sm} ${theme.buttonGhost}`}
            >
              <RefreshCw className="w-4 h-4 inline mr-1" />
              Reset
            </button>
          )}
        </div>

        {/* Progress Steps - Always visible, clickable to navigate */}
        <div className="flex items-center justify-center gap-x-2 text-sm">
          {(() => {
            const hasSchedule = apiRounds.length > 0;

            // Check if all rounds are completed
            const allRoundsCompleted = hasSchedule && apiRounds.every(round =>
              round.games?.length > 0 && round.games.every(game =>
                game.status === 'completed' || game.status === 'Completed' || game.checkin_status === 'completed'
              )
            );

            const steps = allRoundsCompleted
              ? ['select', 'configure', 'preview', 'schedule', 'completed']
              : hasSchedule
                ? ['select', 'configure', 'preview', 'schedule']
                : ['select', 'configure', 'preview'];
            const stepLabels = {
              select: 'Select Teams',
              configure: 'Choose Format',
              preview: 'Preview',
              schedule: 'Schedule',
              completed: 'Completed'
            };

            return steps.map((step, index) => {
              // Determine current step index
              let currentStepIndex;
              if (allRoundsCompleted) {
                currentStepIndex = 4; // Completed step when all rounds are done
              } else if (hasSchedule && scheduleStep === 'select' && !generatedSchedule) {
                currentStepIndex = 3; // Schedule step is current
              } else if (generatedSchedule) {
                currentStepIndex = 2; // Preview step when showing local preview
              } else {
                currentStepIndex = steps.indexOf(scheduleStep);
              }

              const isCompleted = hasSchedule ? index < 3 : (generatedSchedule ? true : index < currentStepIndex);
              const isCurrent = index === currentStepIndex;

              // When viewing existing schedule, steps should not be clickable
              const isViewingSchedule = hasSchedule && scheduleStep === 'select' && !generatedSchedule;

              return (
                <React.Fragment key={step}>
                  <button
                    onClick={() => {
                      // Don't allow step navigation when viewing existing schedule, on schedule step, or completed step
                      if (step === 'schedule' || step === 'completed' || isViewingSchedule) return;
                      // Clear generated schedule and go to the selected step
                      if (generatedSchedule) {
                        setGeneratedSchedule(null);
                      }
                      setScheduleStep(step);
                    }}
                    className={`flex items-center gap-x-1 px-3 py-1 rounded-full transition-all ${
                      step === 'schedule' || step === 'completed' || isViewingSchedule ? 'cursor-default' : 'cursor-pointer'
                    } ${
                      isCurrent
                        ? `${theme.gradient} text-white`
                        : isCompleted
                          ? `${theme.gradient} text-white opacity-80 hover:opacity-100`
                          : `${theme.card} ${theme.text} border ${theme.border} ${theme.cardHover}`
                    }`}
                  >
                    <span className="font-medium">{index + 1}</span>
                    <span className="hidden sm:inline">{stepLabels[step]}</span>
                  </button>
                  {index < steps.length - 1 && <ChevronRight className={`w-4 h-4 ${theme.textMuted}`} />}
                </React.Fragment>
              );
            });
          })()}
        </div>
      </div>

      {/* Display Saved Schedule from API */}
      {apiRounds.length > 0 && !generatedSchedule && scheduleStep === 'select' && (
        <div className="space-y-4">
          {/* Schedule Summary */}
          <div className={`${theme.backgroundSecondary} rounded-lg p-4`}>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${theme.gradient} text-white`}>
                {tournament?.schedule_type || 'Schedule'}
              </div>
              <span className={`text-sm ${theme.textSecondary}`}>
                {apiRounds.reduce((acc, r) => acc + (r.games?.length || 0), 0)} games
              </span>
              <span className={`text-sm ${theme.textSecondary}`}>
                {apiRounds.length} rounds
              </span>
              <PredictAllButton
                games={apiRounds.flatMap(round => (round.games || []).map(g => ({ ...g, round_name: round.round_name })))}
                sportTypeId={predictionSportTypeId}
                sportName={predictionSportName}
                className="ml-auto"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Only show Regenerate/Delete if no games have started and tournament not completed */}
              {(() => {
                const hasGamesStarted = apiRounds.some(round =>
                  round.games?.some(game =>
                    ['in_progress', 'completed'].includes(game.status)
                  )
                );
                const isTournamentCompleted = tournament?.status === 'completed';
                const isTournamentInProgress = tournament?.status === 'in_progress';

                // Check for IPL playoff seeding regardless of game status
                const playoffRounds = apiRounds.filter(r => ['qualifier', 'eliminator'].includes(r.round_type));
                // Detect IPL format either by schedule_type or presence of qualifier/eliminator rounds
                const isIPLFormat = tournament?.schedule_type === 'ipl_style' || playoffRounds.length > 0;
                const leagueRounds = apiRounds.filter(r => r.round_type === 'round_robin');
                const allLeagueMatchesCompleted = leagueRounds.length > 0 && leagueRounds.every(round =>
                  round.games?.every(game =>
                    game.status === 'completed' || game.status === 'Completed' || game.checkin_status === 'completed'
                  )
                );
                const playoffsNotSeeded = playoffRounds.some(round =>
                  round.games?.some(game => !game.participant_a_id || !game.participant_b_id)
                );
                const shouldShowSeedButton = isIPLFormat && allLeagueMatchesCompleted && playoffsNotSeeded;

                // Debug: log all rounds with their types


                if (isTournamentCompleted) {
                  return (
                    <div className="flex items-center gap-x-2">
                      <Trophy className="w-4 h-4 text-yellow-500" />
                      <div className={`text-xs ${theme.textMuted} italic`}>
                        Tournament Completed
                      </div>
                    </div>
                  );
                }

                if (hasGamesStarted) {
                  return (
                    <div className={`text-xs ${theme.textMuted} italic`}>
                      Schedule locked (games in progress)
                    </div>
                  );
                }

                const canModifySchedule = !isTournamentInProgress;

                if (isTournamentInProgress) {
                  return (
                    <div className={`text-xs ${theme.textMuted} italic`}>
                      Tournament in progress (schedule locked)
                    </div>
                  );
                }

                return (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        // Reset state for regeneration
                        setApiRounds([]);
                        setScheduleStep('select');
                        setSelectedFormat(null);
                        setFormatOptions({});
                        setGeneratedSchedule(null);
                        setSelectedParticipantsForSchedule([]);
                      }}
                      disabled={!canModifySchedule}
                      className={`${btn.base} ${btn.sm} ${theme.buttonGhost}`}
                      title={!canModifySchedule ? 'Tournament in progress - schedule is locked' : ''}
                    >
                      <RefreshCw className="w-4 h-4 inline mr-1" />
                      Regenerate
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm('Are you sure you want to delete the schedule? This will remove all games, events, and standings.')) {
                          return;
                        }
                        try {
                          const result = await tournamentAPI.deleteSchedule(tournament.id);
                          toast.success(result.message || 'Schedule deleted successfully');
                          // Clear local state
                          setApiRounds([]);
                          setScheduleStep('select');
                        } catch (error) {
                          console.error('Failed to delete schedule:', error);
                          toast.error(error.message || 'Failed to delete schedule');
                        }
                      }}
                      disabled={!canModifySchedule}
                      className={`${btn.base} ${btn.sm} ${theme.buttonDanger}`}
                      title={!canModifySchedule ? 'Tournament in progress - schedule is locked' : ''}
                    >
                      <Trash2 className="w-4 h-4 inline mr-1" />
                      Delete
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Start Tournament Button - Show when schedule is generated but tournament not started */}
          {(() => {
            const hasSchedule = apiRounds.length > 0;
            const tournamentStatus = tournament?.status || '';
            const canStartTournament = hasSchedule &&
              (tournamentStatus === 'draft' || tournamentStatus === 'registration_open') &&
              (isOrganizer || isScorer);

            return canStartTournament && (
              <div className={`${theme.backgroundSecondary} rounded-lg p-6 border-2 border-blue-500 mb-6`}>
                <div className="flex flex-col items-center space-y-4">
                  <div className="flex items-center gap-x-2">
                    <Play className="w-6 h-6 text-blue-500" />
                    <h3 className={`text-lg font-semibold ${theme.text}`}>Ready to Start Tournament?</h3>
                  </div>
                  <p className={`text-sm ${theme.textMuted} text-center max-w-md`}>
                    Starting the tournament will lock registrations and prevent participants from withdrawing.
                  </p>
                  <button
                    onClick={() => setShowStartTournamentDialog(true)}
                    className={`${btn.base} ${btn.lg} ${theme.buttonPrimary} flex items-center gap-x-2`}
                  >
                    <Play className="w-5 h-5" />
                    <span>Start Tournament</span>
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Add Round Button — restricted to creator/organizer, co-owner, and
              scorer via canManageMatches (folded into isEnabled/ROUND_ADD). */}
          {!!tournamentVO && isEnabled(user?.id, TOURNAMENT_ENTITIES.ROUND_ADD, tournamentVO) && (
            <div className={`${theme.backgroundSecondary} rounded-lg p-4`}>
              <button
                onClick={() => {
                  setNewRoundData({ name: '', type: 'round_robin' });
                  setShowAddRoundModal(true);
                }}
                className={`w-full px-4 py-3 border-2 border-dashed ${theme.border} rounded-lg ${theme.cardHover} flex items-center justify-center gap-2 ${theme.text}`}
              >
                <Plus className="w-5 h-5" />
                Add Round
              </button>
            </div>
          )}

          {/* Rounds & Games from API - Grouped by League/Playoffs */}
          {(() => {
            const isGroupKnockout = tournament?.schedule_type === 'group_knockout';
            // Pool Play rounds are ordered per-group (round_number restarts
            // at 1 in each pool — see groupGamesByRound), so each group's
            // rounds sort into their own contiguous block ahead of the next
            // group's, instead of interleaving Group A/B/C's "Round 1"s.
            const leagueRounds = apiRounds
              .filter(r => r.round_type === 'round_robin')
              .sort((a, b) => (a.group_name || '').localeCompare(b.group_name || '') || a.round_number - b.round_number);
            const playoffRoundsRaw = apiRounds.filter(r => ['qualifier', 'eliminator', 'knockout'].includes(r.round_type));

            // The third-place playoff shares round_number with the Final
            // (see backend scheduleGenerator.generateSingleElimination), so
            // groupGamesByRound (tournamentApi.js) merges it into the same
            // round-group as the Final. Split it out by the individual
            // game's own round_type — each game keeps its own round_type
            // even when grouped — so it renders as its own section below
            // the main bracket instead of inside the Final's row.
            const thirdPlaceGames = playoffRoundsRaw.flatMap(r => (r.games || []).filter(g => g.round_type === 'third_place'));
            const playoffRounds = playoffRoundsRaw.map(r => ({
              ...r,
              games: (r.games || []).filter(g => g.round_type !== 'third_place'),
            }));
            const thirdPlaceRounds = thirdPlaceGames.length > 0 ? [{
              round_number: thirdPlaceGames[0].round_number,
              round_name: thirdPlaceGames[0].round_name || 'Third Place Playoff',
              round_type: 'third_place',
              games: thirdPlaceGames,
            }] : [];

            // Consolation rounds get their own round_number range past the
            // main bracket (no merge issue), so a plain top-level filter is
            // enough — also match on group_name in case a round is tagged
            // that way without round_type (belt & suspenders per KYN-234).
            const consolationRounds = apiRounds.filter(r => r.round_type === 'consolation' || r.group_name === 'consolation');

            // Check for IPL playoff seeding
            const isIPLFormat = tournament?.schedule_type === 'ipl_style' || playoffRounds.some(r => ['qualifier', 'eliminator'].includes(r.round_type));
            const allLeagueMatchesCompleted = leagueRounds.length > 0 && leagueRounds.every(round =>
              round.games?.every(game =>
                game.status === 'completed' || game.status === 'Completed' || game.checkin_status === 'completed'
              )
            );
            const playoffsNotSeeded = playoffRounds.some(round =>
              round.games?.some(game => !game.participant_a_id || !game.participant_b_id)
            );
            const shouldShowSeedButton = isIPLFormat && allLeagueMatchesCompleted && playoffsNotSeeded;

            // Renders a section (League / Playoffs / all rounds) — card layout on mobile, table on desktop
            const renderScheduleTable = (rounds, sectionLabel, sectionCollapsed, onToggleSection, sectionCompleted) => {
              const totalGames = rounds.reduce((acc, r) => acc + (r.games?.length || 0), 0);

              // Shared per-round / per-game helpers used in both layouts
              // Round/match management (add, edit, delete, reorder) is restricted to
              // creator/organizer, co-owner, and scorer — isEnabled() below folds in
              // that role check (via canManageMatches) alongside the existing game-state
              // rules, so nobody else even sees these buttons.
              const getRoundMeta = (round) => {
                const roundCollapsed = collapsedRounds.has(round.roundKey);
                // Add/Delete Round act on the backend purely by round_number
                // (tournament_id + round_number), which group_knockout pools
                // share across groups — every pool's "Round 1" has
                // round_number 1. Allowing those actions here would silently
                // add/delete matches in every other pool's same-numbered
                // round too, not just this one, so they're disabled for
                // group-stage rounds until the backend can scope by group.
                const isAmbiguousGroupRound = round.round_type === 'round_robin' && !!round.group_name;
                const canAddMatch = !isAmbiguousGroupRound && !!tournamentVO && isEnabled(user?.id, TOURNAMENT_ENTITIES.MATCH_ADD, tournamentVO, { round });
                const canDeleteRound = !isAmbiguousGroupRound && !!tournamentVO && isEnabled(user?.id, TOURNAMENT_ENTITIES.ROUND_DELETE, tournamentVO, { round });
                const allRoundGamesCompleted = round.games?.length > 0 && round.games.every(g =>
                  g.status === 'completed' || g.status === 'Completed' || g.checkin_status === 'completed'
                );
                return { roundCollapsed, canAddMatch, canDeleteRound, allRoundGamesCompleted, isAmbiguousGroupRound };
              };

              const getGameMeta = (game, gameIndex, round) => {
                const isCompleted = game.status === 'Completed' || game.status === 'completed' || game.checkin_status === 'completed';
                const canDel = !!tournamentVO && isEnabled(user?.id, TOURNAMENT_ENTITIES.MATCH_DELETE, tournamentVO, { game });
                const canEdit = !!tournamentVO && isEnabled(user?.id, TOURNAMENT_ENTITIES.MATCH_EDIT, tournamentVO, { game });
                const canUp = gameIndex > 0 && canDel;
                const canDown = gameIndex < (round.games?.length ?? 0) - 1 && canDel;
                const canSchedule = !!tournamentVO && isEnabled(user?.id, TOURNAMENT_ENTITIES.MATCH_SCHEDULE, tournamentVO, { game });
                const isExpanded = collapsedMatches.has(game.id);
                return { isCompleted, canDel, canEdit, canUp, canDown, canSchedule, isExpanded };
              };

              const renderStatusBadge = (game, isCompleted) => {
                if (game.checkin_status === 'live' && !isCompleted) return (
                  <button onClick={() => handleOpenLiveTab(game)} className="px-1.5 py-0.5 rounded-full text-xs bg-red-500 text-white animate-pulse flex items-center gap-1"><Radio className="w-2.5 h-2.5" /> Live</button>
                );
                if (isCompleted) return <span className="px-1.5 py-0.5 rounded-full text-xs bg-green-600 text-white">Done</span>;
                if (isScorer) {
                  // A completed tournament must NOT allow starting a new
                  // scoring session on a leftover incomplete game — only
                  // 'in_progress' should enable the button (previously also
                  // included 'completed', which let it stay active/clickable
                  // even after the tournament had already ended).
                  const tournamentStarted = tournament?.status === 'in_progress';
                  return tournamentStarted
                    ? <button onClick={() => setPendingStartScoringGame(game)} className="px-1.5 py-0.5 rounded-full text-xs bg-blue-600 text-white flex items-center gap-1"><Radio className="w-2.5 h-2.5" /> Start Scoring</button>
                    : <span className="px-1.5 py-0.5 rounded-full text-xs bg-gray-400 text-white cursor-not-allowed opacity-60 flex items-center gap-1"><Radio className="w-2.5 h-2.5" /> Start Scoring</span>;
                }
                if (game.event?.start_time) return <span className={`px-1.5 py-0.5 rounded-full text-xs ${theme.gradient} text-white`}>Scheduled</span>;
                return <span className="px-1.5 py-0.5 rounded-full text-xs bg-gray-500 text-white">Pending</span>;
              };

              // Compact "required"/"not needed" tag for a Dreambreaker game,
              // shown next to its status badge without needing to expand the
              // row — full detail (the score line) is in renderGameCard.
              const renderDreambreakerTag = (game) => {
                if (game.game_type !== 'dreambreaker') return null;
                const dbStatus = mlpDreambreakerStatusByGameId[game.id];
                if (!dbStatus?.ready) return null;
                return dbStatus.required
                  ? <span className="px-1.5 py-0.5 rounded-full text-xs bg-amber-500 text-white">Required</span>
                  : <span className="px-1.5 py-0.5 rounded-full text-xs bg-gray-400 text-white">Not needed</span>;
              };

              const renderGameCard = (game) => (
                <div className="p-3 bg-black/5 dark:bg-white/5 border-t border-gray-300 dark:border-gray-600">
                  {game.game_type === 'dreambreaker' && (() => {
                    const dbStatus = mlpDreambreakerStatusByGameId[game.id];
                    if (!dbStatus) return null;
                    if (!dbStatus.ready) {
                      return (
                        <p className={`text-xs ${theme.textMuted} mb-2`}>
                          Whether the Dreambreaker is needed will show here once the other 4 games in this series are complete.
                        </p>
                      );
                    }
                    return dbStatus.required ? (
                      <p className="text-xs text-amber-500 mb-2">
                        Dreambreaker required — series tied {dbStatus.aWins}-{dbStatus.bWins} after the first 4 games.
                      </p>
                    ) : (
                      <p className={`text-xs ${theme.textMuted} mb-2`}>
                        Dreambreaker not needed — series already decided {Math.max(dbStatus.aWins, dbStatus.bWins)}-{Math.min(dbStatus.aWins, dbStatus.bWins)} after the first 4 games.
                      </p>
                    );
                  })()}
                  <GameCard
                    game={game}
                    tournament={tournament}
                    tournamentVO={tournamentVO}
                    participants={participants}
                    userTeamIds={userTeamIds}
                    isScorer={isScorer}
                    externalEditMode={editingParticipantsForGame === game.id}
                    onExitEditMode={() => setEditingParticipantsForGame(null)}
                    onCheckInSuccess={handleCheckInSuccess}
                    onStartGame={handleGameStart}
                    onOpenLive={handleOpenLiveTab}
                    onStartScoring={setPendingStartScoringGame}
                    onParticipantUpdate={() => tournamentScheduleAPI.getRounds(tournament.id).then(setApiRounds)}
                  />
                </div>
              );

              return (
                <div className="rounded-lg overflow-hidden border border-gray-400 dark:border-gray-500">
                  {/* Section header — shared */}
                  <div
                    className={`px-4 py-2.5 flex items-center justify-between cursor-pointer ${theme.backgroundSecondary} border-b border-gray-400 dark:border-gray-500`}
                    onClick={onToggleSection}
                  >
                    <div className="flex items-center gap-2">
                      <ChevronRight className={`w-4 h-4 ${theme.textMuted} transition-transform ${sectionCollapsed ? '' : 'rotate-90'}`} />
                      <span className={`font-semibold ${theme.text}`}>{sectionLabel}</span>
                      <span className={`text-xs ${theme.textMuted}`}>({rounds.length} rounds · {totalGames} games)</span>
                    </div>
                    {sectionCompleted && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500 text-white">Completed</span>
                    )}
                  </div>

                  {!sectionCollapsed && (
                    <>
                      {/* ── MOBILE: card layout ── */}
                      <div className="md:hidden divide-y divide-gray-300 dark:divide-gray-600">
                        {rounds.map((round, roundIdx) => {
                          const { roundCollapsed, canAddMatch, canDeleteRound, allRoundGamesCompleted } = getRoundMeta(round);
                          const isNewGroup = round.round_type === 'round_robin' && !!round.group_name && round.group_name !== rounds[roundIdx - 1]?.group_name;
                          return (
                            <React.Fragment key={round.roundKey}>
                              {/* Group divider — separates each pool's rounds
                                  within the Pool Play section (rounds are
                                  pre-sorted by group above) */}
                              {isNewGroup && (
                                <div className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wide ${theme.textMuted} ${theme.backgroundSecondary}`}>
                                  Group {round.group_name}
                                </div>
                              )}
                              {/* Round header */}
                              <div
                                className="px-3 py-2 flex items-center justify-between cursor-pointer bg-black/10 dark:bg-white/5"
                                onClick={() => toggleRoundCollapse(round.roundKey)}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 ${theme.textMuted} transition-transform ${roundCollapsed ? '' : 'rotate-90'}`} />
                                  <span className={`font-medium text-xs ${theme.text}`}>{round.round_name?.replace(/^Matchday\s+/i, 'Round ')}</span>
                                  <span className={`text-xs ${theme.textMuted} truncate`}>· {round.games?.length || 0} game(s){round.group_name && ` · Group ${round.group_name}`}</span>
                                  {allRoundGamesCompleted && <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full text-xs bg-blue-500 text-white">Done</span>}
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                                  {canAddMatch && (
                                    <button onClick={() => { setSelectedRoundForNewMatch(round.round_number); setNewMatchData({ participantA: null, participantB: null }); setShowAddMatchModal(true); }} className={`w-6 h-6 flex items-center justify-center rounded ${theme.gradient} text-white`} title="Add match"><Plus className="w-3 h-3" /></button>
                                  )}
                                  {canDeleteRound && (
                                    <button onClick={() => handleDeleteRound(round.round_number)} className="w-6 h-6 flex items-center justify-center rounded border border-red-400 dark:border-red-600 text-red-500" title="Delete round"><Trash2 className="w-3 h-3" /></button>
                                  )}
                                </div>
                              </div>
                              {/* Match cards */}
                              {!roundCollapsed && (
                                round.games?.length > 0 ? (
                                  <div className="divide-y divide-gray-300 dark:divide-gray-600">
                                    {round.games.map((game, gameIndex) => {
                                      const { isCompleted, canDel, canEdit, canUp, canDown, canSchedule, isExpanded } = getGameMeta(game, gameIndex, round);
                                      return (
                                        <React.Fragment key={game.id}>
                                          <div
                                            className={`px-3 py-3 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-500/5' : ''} hover:bg-black/5 dark:hover:bg-white/5`}
                                            onClick={() => toggleMatchCollapse(game.id)}
                                          >
                                            {/* Match number + status */}
                                            <div className="flex items-center justify-between mb-2">
                                              <span className={`text-xs ${theme.textMuted}`}>
                                                #{game.match_number} · {round.round_name?.replace(/^Matchday\s+/i, 'Round ')}
                                                {game.game_type_label && ` · ${game.game_type_label}`}
                                              </span>
                                              <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                                {renderDreambreakerTag(game)}
                                                {renderStatusBadge(game, isCompleted)}
                                              </div>
                                            </div>
                                            {/* Home vs Away */}
                                            <div className="grid grid-cols-3 items-center gap-1 mb-2.5">
                                              <div className="min-w-0">
                                                <div className="flex items-center justify-end gap-1 min-w-0">
                                                  {game.participant_a_type === 'team' && game.participant_a_id && (
                                                    <button onClick={(e) => handleShowTeamRoster(e, game.participant_a_id, game.participant_a_name)} className={`flex-shrink-0 ${theme.textMuted} hover:text-blue-400`} title="View players"><Info className="w-3 h-3" /></button>
                                                  )}
                                                  <span className={`text-sm font-semibold truncate ${game.winner_id === game.participant_a_id && game.winner_id ? 'text-green-400' : theme.text}`}>{game.participant_a_name || 'TBD'}</span>
                                                </div>
                                                {game.participant_a_players?.length > 0 && (
                                                  <div className={`text-xs ${theme.textMuted} truncate text-right`}>
                                                    {game.participant_a_players.map(p => p.name.split(' ')[0]).join(' / ')}
                                                  </div>
                                                )}
                                              </div>
                                              <div className="text-center">
                                                {isCompleted && game.participant_a_score !== null && game.participant_b_score !== null
                                                  ? <span className={`text-sm font-bold ${theme.text}`}>{game.participant_a_score} – {game.participant_b_score}</span>
                                                  : <span className={`text-xs font-semibold ${theme.textMuted}`}>vs</span>}
                                              </div>
                                              <div className="min-w-0">
                                                <div className="flex items-center justify-start gap-1 min-w-0">
                                                  <span className={`text-sm font-semibold truncate ${game.winner_id === game.participant_b_id && game.winner_id ? 'text-green-400' : theme.text}`}>{game.participant_b_name || 'TBD'}</span>
                                                  {game.participant_b_type === 'team' && game.participant_b_id && (
                                                    <button onClick={(e) => handleShowTeamRoster(e, game.participant_b_id, game.participant_b_name)} className={`flex-shrink-0 ${theme.textMuted} hover:text-blue-400`} title="View players"><Info className="w-3 h-3" /></button>
                                                  )}
                                                </div>
                                                {game.participant_b_players?.length > 0 && (
                                                  <div className={`text-xs ${theme.textMuted} truncate`}>
                                                    {game.participant_b_players.map(p => p.name.split(' ')[0]).join(' / ')}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                            {/* Action buttons */}
                                            <div className="flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                                              <div className="flex items-center gap-1">
                                                {canUp ? <button onClick={() => handleMoveMatch(game, 'up')} className={`w-7 h-7 flex items-center justify-center rounded border border-gray-400 dark:border-gray-500 ${theme.textMuted}`} title="Move up"><ChevronUp className="w-3.5 h-3.5" /></button> : <span className="w-7 h-7" />}
                                                {canDown ? <button onClick={() => handleMoveMatch(game, 'down')} className={`w-7 h-7 flex items-center justify-center rounded border border-gray-400 dark:border-gray-500 ${theme.textMuted}`} title="Move down"><ChevronDown className="w-3.5 h-3.5" /></button> : <span className="w-7 h-7" />}
                                                {canEdit ? <button onClick={() => { setEditingParticipantsForGame(game.id); setCollapsedMatches(prev => { const s = new Set(prev); s.add(game.id); return s; }); }} className="w-7 h-7 flex items-center justify-center rounded border border-gray-400 dark:border-gray-500 text-blue-400" title="Edit teams"><Edit2 className="w-3.5 h-3.5" /></button> : <span className="w-7 h-7" />}
                                                {canDel ? <button onClick={() => handleDeleteMatch(game.id)} className="w-7 h-7 flex items-center justify-center rounded border border-red-400 dark:border-red-600 text-red-500" title="Delete match"><Trash2 className="w-3.5 h-3.5" /></button> : <span className="w-7 h-7" />}
                                              </div>
                                              {canSchedule && (
                                                <button onClick={() => handleEditMatch(game)} className={`w-7 h-7 flex items-center justify-center rounded ${theme.gradient} text-white`} title={game.event?.start_time ? 'Edit schedule' : 'Schedule match'}><Calendar className="w-3.5 h-3.5" /></button>
                                              )}
                                              {canSchedule && (
                                                <button onClick={() => handleOpenMatchSettings(game)} className={`w-7 h-7 flex items-center justify-center rounded border ${theme.border} ${theme.text}`} title="Match settings override"><Settings className="w-3.5 h-3.5" /></button>
                                              )}
                                            </div>
                                          </div>
                                          {isExpanded && renderGameCard(game)}
                                        </React.Fragment>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div className={`px-3 py-3 text-center text-xs ${theme.textMuted} italic`}>Determined by previous round results</div>
                                )
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>

                      {/* ── DESKTOP: original table layout ── */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm border-collapse min-w-[580px]">
                          <thead>
                            <tr className={`border-b-2 border-gray-400 dark:border-gray-500 ${theme.backgroundSecondary}`}>
                              <th className={`px-3 py-2 text-left text-xs font-semibold ${theme.textMuted} w-10 border-r border-gray-400 dark:border-gray-500`}>#</th>
                              <th className={`px-3 py-2 text-left text-xs font-semibold ${theme.textMuted} w-28 border-r border-gray-400 dark:border-gray-500`}>Round</th>
                              <th className={`px-3 py-2 text-left text-xs font-semibold ${theme.textMuted} border-r border-gray-400 dark:border-gray-500`}>Home</th>
                              <th className={`px-3 py-2 text-center text-xs font-semibold ${theme.textMuted} w-20 border-r border-gray-400 dark:border-gray-500`}>Score</th>
                              <th className={`px-3 py-2 text-left text-xs font-semibold ${theme.textMuted} border-r border-gray-400 dark:border-gray-500`}>Away</th>
                              <th className={`px-3 py-2 text-center text-xs font-semibold ${theme.textMuted} w-36 border-r border-gray-400 dark:border-gray-500`}>Status</th>
                              <th className={`px-3 py-2 text-center text-xs font-semibold ${theme.textMuted} w-28`}>
                                <div className="grid grid-cols-4 gap-1 justify-items-center">
                                  <ChevronUp className="w-3 h-3" /><ChevronDown className="w-3 h-3" /><Edit2 className="w-3 h-3" /><Trash2 className="w-3 h-3" />
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {rounds.map((round, roundIdx) => {
                              const { roundCollapsed, canAddMatch, canDeleteRound, allRoundGamesCompleted } = getRoundMeta(round);
                              const isNewGroup = round.round_type === 'round_robin' && !!round.group_name && round.group_name !== rounds[roundIdx - 1]?.group_name;
                              return (
                                <React.Fragment key={round.roundKey}>
                                  {/* Group divider — separates each pool's
                                      rounds within the Pool Play section */}
                                  {isNewGroup && (
                                    <tr>
                                      <td colSpan={7} className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wide ${theme.textMuted} ${theme.backgroundSecondary} border-b border-gray-300 dark:border-gray-600`}>
                                        Group {round.group_name}
                                      </td>
                                    </tr>
                                  )}
                                  {/* Round header row */}
                                  <tr className="border-b border-gray-300 dark:border-gray-600 cursor-pointer bg-black/10 dark:bg-white/5 hover:bg-black/15 dark:hover:bg-white/10 transition-colors" onClick={() => toggleRoundCollapse(round.roundKey)}>
                                    <td className="px-3 py-2 border-r border-gray-300 dark:border-gray-600">
                                      <ChevronRight className={`w-3.5 h-3.5 ${theme.textMuted} transition-transform ${roundCollapsed ? '' : 'rotate-90'}`} />
                                    </td>
                                    <td colSpan={4} className="px-3 py-2 border-r border-gray-300 dark:border-gray-600">
                                      <div className="flex items-center gap-2">
                                        <span className={`font-medium text-xs ${theme.text}`}>{round.round_name?.replace(/^Matchday\s+/i, 'Round ')}</span>
                                        <span className={`text-xs ${theme.textMuted}`}>· {round.games?.length || 0} game(s){round.group_name && ` · Group ${round.group_name}`}</span>
                                        {allRoundGamesCompleted && <span className="px-1.5 py-0.5 rounded-full text-xs bg-blue-500 text-white">Completed</span>}
                                      </div>
                                    </td>
                                    <td className="px-3 py-2 border-r border-gray-300 dark:border-gray-600" />
                                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                      <div className="grid grid-cols-4 gap-1 justify-items-center">
                                        {canAddMatch ? (
                                          <button onClick={() => { setSelectedRoundForNewMatch(round.round_number); setNewMatchData({ participantA: null, participantB: null }); setShowAddMatchModal(true); }} className={`w-6 h-6 flex items-center justify-center rounded ${theme.gradient} text-white hover:shadow transition-all`} title="Add match"><Plus className="w-3 h-3" /></button>
                                        ) : <span className="w-6 h-6" />}
                                        <span className="w-6 h-6" />
                                        <span className="w-6 h-6" />
                                        {canDeleteRound ? (
                                          <button onClick={() => handleDeleteRound(round.round_number)} className="w-6 h-6 flex items-center justify-center rounded border border-red-400 dark:border-red-600 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all" title="Delete round"><Trash2 className="w-3 h-3" /></button>
                                        ) : <span className="w-6 h-6" />}
                                      </div>
                                    </td>
                                  </tr>
                                  {/* Match rows */}
                                  {!roundCollapsed && (
                                    round.games?.length > 0 ? round.games.map((game, gameIndex) => {
                                      const { isCompleted, canDel, canEdit, canUp, canDown, canSchedule, isExpanded } = getGameMeta(game, gameIndex, round);
                                      return (
                                        <React.Fragment key={game.id}>
                                          <tr className={`border-b border-gray-300 dark:border-gray-600 last:border-0 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-500/5' : ''} hover:bg-black/5 dark:hover:bg-white/5`} onClick={() => toggleMatchCollapse(game.id)}>
                                            <td className={`px-3 py-2 text-xs ${theme.textMuted} border-r border-gray-300 dark:border-gray-600 text-center`}>{game.match_number}</td>
                                            <td className={`px-3 py-2 text-xs ${theme.textMuted} border-r border-gray-300 dark:border-gray-600`}>
                                              {round.round_name?.replace(/^Matchday\s+/i, 'Round ')}
                                              {game.game_type_label && <div className="text-xs opacity-75">{game.game_type_label}</div>}
                                            </td>
                                            <td className={`px-3 py-2 border-r border-gray-300 dark:border-gray-600 font-medium text-sm ${game.winner_id === game.participant_a_id && game.winner_id ? 'text-green-400' : theme.text}`}>
                                              <div className="flex items-center gap-1">
                                                <span className="truncate">{game.participant_a_name || 'TBD'}</span>
                                                {game.participant_a_type === 'team' && game.participant_a_id && (
                                                  <button onClick={(e) => handleShowTeamRoster(e, game.participant_a_id, game.participant_a_name)} className={`flex-shrink-0 ${theme.textMuted} hover:text-blue-400`} title="View players"><Info className="w-3 h-3" /></button>
                                                )}
                                              </div>
                                              {game.participant_a_players?.length > 0 && (
                                                <div className={`text-xs ${theme.textMuted} truncate`}>
                                                  {game.participant_a_players.map(p => p.name.split(' ')[0]).join(' / ')}
                                                </div>
                                              )}
                                            </td>
                                            <td className="px-3 py-2 text-center border-r border-gray-300 dark:border-gray-600">
                                              {isCompleted && game.participant_a_score !== null && game.participant_b_score !== null
                                                ? <span className={`text-sm font-bold ${theme.text}`}>{game.participant_a_score} – {game.participant_b_score}</span>
                                                : <span className={`text-xs ${theme.textMuted}`}>vs</span>}
                                            </td>
                                            <td className={`px-3 py-2 border-r border-gray-300 dark:border-gray-600 font-medium text-sm ${game.winner_id === game.participant_b_id && game.winner_id ? 'text-green-400' : theme.text}`}>
                                              <div className="flex items-center gap-1">
                                                <span className="truncate">{game.participant_b_name || 'TBD'}</span>
                                                {game.participant_b_type === 'team' && game.participant_b_id && (
                                                  <button onClick={(e) => handleShowTeamRoster(e, game.participant_b_id, game.participant_b_name)} className={`flex-shrink-0 ${theme.textMuted} hover:text-blue-400`} title="View players"><Info className="w-3 h-3" /></button>
                                                )}
                                              </div>
                                              {game.participant_b_players?.length > 0 && (
                                                <div className={`text-xs ${theme.textMuted} truncate`}>
                                                  {game.participant_b_players.map(p => p.name.split(' ')[0]).join(' / ')}
                                                </div>
                                              )}
                                            </td>
                                            <td className="px-3 py-2 border-r border-gray-300 dark:border-gray-600" onClick={(e) => e.stopPropagation()}>
                                              <div className="flex items-center justify-center gap-1.5">
                                                {renderDreambreakerTag(game)}
                                                {renderStatusBadge(game, isCompleted)}
                                                {canSchedule && (
                                                  <button onClick={() => handleEditMatch(game)} className={`w-6 h-6 flex items-center justify-center rounded ${theme.gradient} text-white hover:shadow transition-all`} title={game.event?.start_time ? 'Edit schedule' : 'Schedule match'}><Calendar className="w-3 h-3" /></button>
                                                )}
                                                {canSchedule && (
                                                  <button onClick={() => handleOpenMatchSettings(game)} className={`w-6 h-6 flex items-center justify-center rounded border ${theme.border} ${theme.text} hover:shadow transition-all`} title="Match settings override"><Settings className="w-3 h-3" /></button>
                                                )}
                                              </div>
                                            </td>
                                            <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                                              <div className="grid grid-cols-4 gap-1 justify-items-center">
                                                {canUp ? <button onClick={() => handleMoveMatch(game, 'up')} className={`w-6 h-6 flex items-center justify-center rounded border border-gray-400 dark:border-gray-500 ${theme.textMuted} hover:bg-gray-100 dark:hover:bg-gray-800 transition-all`} title="Move up"><ChevronUp className="w-3 h-3" /></button> : <span className="w-6 h-6" />}
                                                {canDown ? <button onClick={() => handleMoveMatch(game, 'down')} className={`w-6 h-6 flex items-center justify-center rounded border border-gray-400 dark:border-gray-500 ${theme.textMuted} hover:bg-gray-100 dark:hover:bg-gray-800 transition-all`} title="Move down"><ChevronDown className="w-3 h-3" /></button> : <span className="w-6 h-6" />}
                                                {canEdit ? <button onClick={() => { setEditingParticipantsForGame(game.id); setCollapsedMatches(prev => { const s = new Set(prev); s.add(game.id); return s; }); }} className="w-6 h-6 flex items-center justify-center rounded border border-gray-400 dark:border-gray-500 text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" title="Edit teams"><Edit2 className="w-3 h-3" /></button> : <span className="w-6 h-6" />}
                                                {canDel ? <button onClick={() => handleDeleteMatch(game.id)} className="w-6 h-6 flex items-center justify-center rounded border border-red-400 dark:border-red-600 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all" title="Delete match"><Trash2 className="w-3 h-3" /></button> : <span className="w-6 h-6" />}
                                              </div>
                                            </td>
                                          </tr>
                                          {isExpanded && (
                                            <tr className="border-b border-gray-300 dark:border-gray-600">
                                              <td colSpan={7} className="p-0">{renderGameCard(game)}</td>
                                            </tr>
                                          )}
                                        </React.Fragment>
                                      );
                                    }) : (
                                      <tr className="border-b border-gray-300 dark:border-gray-600">
                                        <td colSpan={7} className={`px-3 py-3 text-center text-xs ${theme.textMuted} italic`}>Determined by previous round results</td>
                                      </tr>
                                    )
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              );
            };

            return (
              <>
                {leagueRounds.length > 0 && renderScheduleTable(
                  leagueRounds,
                  isGroupKnockout ? 'Pool Play' : 'League Matches',
                  leagueSectionCollapsed,
                  () => setLeagueSectionCollapsed(!leagueSectionCollapsed),
                  allLeagueMatchesCompleted
                )}

                {/* Seed Playoffs Button - Between League and Playoffs */}
                {shouldShowSeedButton && (
                  <div className="flex justify-center py-4">
                    <button
                      onClick={() => setShowSeedPlayoffsDialog(true)}
                      className={`${btn.base} ${btn.lg} ${theme.buttonPrimary} flex items-center gap-2 shadow-lg hover:shadow-xl`}
                      title="Seed playoff matches with top 4 teams"
                    >
                      <Zap className="w-5 h-5" />
                      Seed Playoffs with Top 4 Teams
                    </button>
                  </div>
                )}

                {playoffRounds.length > 0 && renderScheduleTable(
                  playoffRounds,
                  isGroupKnockout ? 'Knockout Bracket' : 'Playoffs',
                  playoffSectionCollapsed,
                  () => setPlayoffSectionCollapsed(!playoffSectionCollapsed),
                  false
                )}

                {/* Third-place playoff + consolation bracket — always after the main bracket, never interleaved */}
                {thirdPlaceRounds.length > 0 && renderScheduleTable(
                  thirdPlaceRounds,
                  'Third Place Playoff',
                  thirdPlaceSectionCollapsed,
                  () => setThirdPlaceSectionCollapsed(!thirdPlaceSectionCollapsed),
                  false
                )}

                {consolationRounds.length > 0 && renderScheduleTable(
                  consolationRounds,
                  'Consolation Bracket',
                  consolationSectionCollapsed,
                  () => setConsolationSectionCollapsed(!consolationSectionCollapsed),
                  false
                )}

                {/* Show all rounds if no league/playoff/third-place/consolation distinction */}
                {leagueRounds.length === 0 && playoffRounds.length === 0 && thirdPlaceRounds.length === 0 && consolationRounds.length === 0 && apiRounds.length > 0 &&
                  renderScheduleTable(
                    apiRounds,
                    'Matches',
                    leagueSectionCollapsed,
                    () => setLeagueSectionCollapsed(!leagueSectionCollapsed),
                    false
                  )
                }
              </>
            );
          })()}
        </div>
      )}

      {/* Step 1: Select Participants */}
      {scheduleStep === 'select' && !generatedSchedule && apiRounds.length === 0 && (
        <>
          {tournament?.status === 'in_progress' ? (
            <div className={`${theme.backgroundSecondary} rounded-lg p-6 border-2 border-blue-500`}>
              <div className="flex flex-col items-center space-y-3 text-center">
                <AlertCircle className="w-12 h-12 text-blue-500" />
                <h4 className={`font-semibold ${theme.text}`}>
                  Tournament In Progress
                </h4>
                <p className={`text-sm ${theme.textMuted} max-w-md`}>
                  The tournament has started. Schedule creation and format selection are locked.
                  You cannot create a new schedule while the tournament is in progress.
                </p>
              </div>
            </div>
          ) : (
            <div className={`${theme.backgroundSecondary} rounded-lg p-6`}>
              <div className="flex items-center justify-between mb-4">
                <h4 className={`font-semibold ${theme.text}`}>
                  Select Teams/Participants for Schedule
                </h4>
                <div className={`text-sm ${theme.textSecondary}`}>
                  {selectedParticipantsForSchedule.length} selected
                </div>
              </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => {
                // Get all enrolled teams (same filter as Enrolled tab)
                const enrolledTeams = participants
                  .filter(p => p.teamId != null)
                  .map(p => ({
                    id: p.teamId,
                    name: p.team?.teamName || p.teamName || `Team ${p.teamId}`,
                    type: 'team',
                    logo: p.team?.teamLogo,
                    data: p
                  }));
                // Get all enrolled individuals (only those with player=true)
                const enrolledIndividuals = participants
                  .filter(p => p.participantId != null && p.teamId == null && p.player === true)
                  .map(p => ({
                    id: p.participantId,
                    name: p.participantName || `Player ${p.participantId}`,
                    type: 'individual',
                    data: p
                  }));
                setSelectedParticipantsForSchedule([...enrolledTeams, ...enrolledIndividuals]);
              }}
              className={`px-3 py-1.5 text-xs border ${theme.border} ${theme.text} rounded-lg ${theme.cardHover}`}
            >
              Select All
            </button>
            <button
              onClick={() => setSelectedParticipantsForSchedule([])}
              className={`px-3 py-1.5 text-xs border ${theme.border} ${theme.text} rounded-lg ${theme.cardHover}`}
            >
              Clear All
            </button>
          </div>

          {/* Participants List */}
          {participants.length === 0 ? (
            <div className={`text-center py-8 ${theme.textMuted}`}>
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No participants registered yet.</p>
              <p className="text-sm mt-1">Participants need to register before creating a schedule.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Teams Section (same filter as Enrolled tab) */}
              {participants.filter(p => p.teamId != null).length > 0 && (
                <div>
                  <h5 className={`text-sm font-medium ${theme.text} mb-2 flex items-center`}>
                    <Users2 className={`w-4 h-4 mr-2 ${theme.link}`} />
                    Teams ({participants.filter(p => p.teamId != null).length})
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {participants
                      .filter(p => p.teamId != null)
                      .map((p) => {
                        const isSelected = selectedParticipantsForSchedule.some(s => s.id === p.teamId && s.type === 'team');
                        return (
                          <div
                            key={`team-${p.id}`}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedParticipantsForSchedule(prev => prev.filter(s => !(s.id === p.teamId && s.type === 'team')));
                              } else {
                                setSelectedParticipantsForSchedule(prev => [...prev, {
                                  id: p.teamId,
                                  name: p.team?.teamName || p.teamName || `Team ${p.teamId}`,
                                  type: 'team',
                                  logo: p.team?.teamLogo,
                                  data: p
                                }]);
                              }
                            }}
                            className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              isSelected
                                ? `border-purple-500 ${theme.backgroundSecondary}`
                                : `${theme.border} ${theme.cardHover}`
                            }`}
                          >
                            <div className="flex items-center gap-x-3">
                              {isSelected ? (
                                <div className={`w-8 h-8 rounded-lg ${theme.gradient} flex items-center justify-center`}>
                                  <Check className="w-5 h-5 text-white" />
                                </div>
                              ) : (
                                <TeamLogo
                                  teamId={p.teamId}
                                  teamLogo={p.team?.teamLogo}
                                  teamName={p.team?.teamName || p.teamName}
                                  size="w-8 h-8"
                                  textSize="text-xs"
                                  rounded="rounded-lg"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className={`font-medium ${theme.text} truncate`}>
                                  {p.team?.teamName || p.teamName || `Team ${p.teamId}`}
                                </div>
                                <div className={`text-xs ${theme.textMuted}`}>
                                  {p.team?.memberCount ? `${p.team.memberCount} members` : `ID: ${p.teamId}`}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Individual Players Section (only those with player=true) */}
              {participants.filter(p => p.participantId != null && p.teamId == null && p.player === true).length > 0 && (
                <div>
                  <h5 className={`text-sm font-medium ${theme.text} mb-2 flex items-center`}>
                    <User className={`w-4 h-4 mr-2 ${theme.link}`} />
                    Individual Players ({participants.filter(p => p.participantId != null && p.teamId == null && p.player === true).length})
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {participants
                      .filter(p => p.participantId != null && p.teamId == null && p.player === true)
                      .map((p) => {
                        const isSelected = selectedParticipantsForSchedule.some(s => s.id === p.participantId && s.type === 'individual');
                        return (
                          <div
                            key={`individual-${p.id}`}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedParticipantsForSchedule(prev => prev.filter(s => !(s.id === p.participantId && s.type === 'individual')));
                              } else {
                                setSelectedParticipantsForSchedule(prev => [...prev, {
                                  id: p.participantId,
                                  name: p.participantName || `Player ${p.participantId}`,
                                  type: 'individual',
                                  data: p
                                }]);
                              }
                            }}
                            className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              isSelected
                                ? `border-purple-500 ${theme.backgroundSecondary}`
                                : `${theme.border} ${theme.cardHover}`
                            }`}
                          >
                            <div className="flex items-center gap-x-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${theme.gradient}`}>
                                {isSelected ? (
                                  <Check className="w-5 h-5 text-white" />
                                ) : (
                                  <span className="text-white font-bold text-sm">
                                    {(p.participantName || 'P')[0].toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className={`font-medium ${theme.text} truncate`}>
                                  {p.participantName || `Player ${p.participantId}`}
                                </div>
                                <div className={`text-xs ${theme.textMuted}`}>
                                  ID: {p.participantId}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div>{/* Empty div for spacing */}</div>
                <button
                  onClick={() => setScheduleStep('configure')}
                  disabled={selectedParticipantsForSchedule.length < 2}
                  className={`px-6 py-2 ${theme.gradient} text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center`}
                >
                  Next: Choose Format
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          )}
            </div>
          )}
        </>
      )}

      {/* Step 2: Configure Format */}
      {scheduleStep === 'configure' && !generatedSchedule && (
        <>
          {tournament?.status === 'in_progress' ? (
            <div className={`${theme.backgroundSecondary} rounded-lg p-6 border-2 border-blue-500`}>
              <div className="flex flex-col items-center space-y-3 text-center">
                <AlertCircle className="w-12 h-12 text-blue-500" />
                <h4 className={`font-semibold ${theme.text}`}>
                  Tournament In Progress
                </h4>
                <p className={`text-sm ${theme.textMuted} max-w-md`}>
                  The tournament has started. Format configuration is locked.
                </p>
              </div>
            </div>
          ) : (
            <div className={`${theme.backgroundSecondary} rounded-lg p-6`}>
              <div className="flex items-center justify-between mb-6">
            <h4 className={`font-semibold ${theme.text}`}>
              Choose Tournament Format
            </h4>
            <div className={`text-sm ${theme.textSecondary}`}>
              {selectedParticipantsForSchedule.length} participants selected
            </div>
          </div>

          {/* Format Selection Dropdown — was a 7-card grid, replaced with a
              select + a single detail panel for whichever format is chosen,
              to cut down the vertical space this step takes. */}
          {(() => {
            // Compass Draw is the first format gated to specific sports
            // (COMPASS_DRAW_FORMAT.sports) rather than just disabled by a
            // roster-size check — per the KYN-208 gap matrix it isn't a
            // generic-sport format, so it's filtered out of the list
            // entirely for sports it doesn't apply to, not shown-disabled.
            const tournamentSportName = (tournament?.sport_typea?.sport_name || tournament?.sportType?.sportName || tournament?.sportName || '').toLowerCase();
            const allFormats = [...TOURNAMENT_FORMATS, CUSTOM_FORMAT, MLP_FORMAT, COMPASS_DRAW_FORMAT]
              .filter(format => !format.sports || format.sports.includes(tournamentSportName));
            const getFormatIcon = (format) => format.icon === 'trophy' ? Trophy
              : format.icon === 'shield' ? Shield
              : format.icon === 'refresh-cw' ? RefreshCw
              : format.icon === 'award' ? Award
              : format.icon === 'zap' ? Zap
              : format.icon === 'git-branch' ? GitBranch
              : format.icon === 'settings' ? Settings
              : format.icon === 'users2' ? Users2
              : format.icon === 'compass' ? Compass
              : Trophy;
            const SelectedIcon = selectedFormat ? getFormatIcon(selectedFormat) : null;
            const selectedIsDisabled = selectedFormat && selectedParticipantsForSchedule.length < selectedFormat.minTeams;

            return (
              <div className="mb-6">
                <select
                  value={selectedFormat?.id || ''}
                  onChange={(e) => {
                    const format = allFormats.find(f => f.id === e.target.value);
                    if (format) setSelectedFormat(format);
                    // Custom Format's smart defaults are applied by the
                    // useEffect below, not here — relying only on this
                    // onChange meant they never recomputed if Custom Format
                    // was already selected (e.g. navigating back to Select
                    // Teams, changing teams, then returning without
                    // re-picking it from this dropdown).
                  }}
                  className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                >
                  <option value="">Select a format...</option>
                  {allFormats.map((format) => {
                    const notEnoughTeams = selectedParticipantsForSchedule.length < format.minTeams;
                    // MLP requires every registered team to actually field 4
                    // players (2 doubles pairs) — teamsHaveExactlyFourPlayers
                    // is null while unknown/loading, so it only disables once
                    // we've actually confirmed a mismatch (never a false
                    // positive from a still-in-flight fetch).
                    const wrongRosterSizeForMLP = format.id === 'mlp' && teamsHaveExactlyFourPlayers === false;
                    const isDisabled = notEnoughTeams || wrongRosterSizeForMLP || format.comingSoon;
                    const disabledReason = format.comingSoon
                      ? ' — coming soon (re-pairing not yet supported)'
                      : notEnoughTeams
                        ? ` — need ${format.minTeams - selectedParticipantsForSchedule.length} more team(s)`
                        : wrongRosterSizeForMLP
                          ? ' — every team must have exactly 4 players'
                          : '';
                    return (
                      <option key={format.id} value={format.id} disabled={isDisabled}>
                        {format.name}
                        {disabledReason}
                      </option>
                    );
                  })}
                </select>

                {selectedFormat && (
                  <div className={`mt-3 p-3 rounded-lg border ${theme.border} ${theme.backgroundSecondary} flex items-start gap-x-3`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme.gradient} flex-shrink-0`}>
                      <SelectedIcon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className={`text-sm ${theme.textMuted}`}>{selectedFormat.description}</div>
                      <div className={`text-xs mt-1 ${selectedIsDisabled ? 'text-red-500' : theme.textSecondary}`}>
                        Min: {selectedFormat.minTeams} teams
                        {selectedIsDisabled && ` (need ${selectedFormat.minTeams - selectedParticipantsForSchedule.length} more)`}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Format-Specific Options */}
          {selectedFormat && (
            <div className={`${theme.card} rounded-lg p-4 border ${theme.border} mb-6`}>
              <h5 className={`font-medium ${theme.text} mb-4`}>Format Options</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Third place playoff + consolation bracket (single elimination only) */}
                {selectedFormat.id === 'single_elimination' && (
                  <>
                    <div>
                      <label className={`flex items-center gap-2 text-sm font-medium ${theme.text} mb-2 cursor-pointer`}>
                        <input
                          type="checkbox"
                          checked={!!formatOptions.thirdPlace}
                          onChange={(e) => setFormatOptions(prev => ({ ...prev, thirdPlace: e.target.checked }))}
                          className="rounded"
                        />
                        Include third-place playoff
                      </label>
                      <p className={`text-xs ${theme.textMuted}`}>
                        Adds a match between the two semifinal losers to decide 3rd place.
                      </p>
                    </div>
                    <div>
                      <label className={`flex items-center gap-2 text-sm font-medium ${theme.text} mb-2 cursor-pointer`}>
                        <input
                          type="checkbox"
                          checked={formatOptions.consolation === 'first_round'}
                          onChange={(e) => setFormatOptions(prev => ({ ...prev, consolation: e.target.checked ? 'first_round' : 'none' }))}
                          className="rounded"
                        />
                        Include consolation bracket
                      </label>
                      <p className={`text-xs ${theme.textMuted}`}>
                        Round-1 losers play a separate mini knockout bracket instead of being eliminated outright.
                      </p>
                    </div>
                  </>
                )}

                {/* Matches per pairing (for round robin formats) */}
                {['round_robin', 'league_plus_finals', 'ipl_style'].includes(selectedFormat.id) && (
                  <div>
                    <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                      Matches per Pairing
                    </label>
                    <select
                      value={formatOptions.matchesPerPairing}
                      onChange={(e) => setFormatOptions(prev => ({
                        ...prev,
                        matchesPerPairing: parseInt(e.target.value)
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                    >
                      <option value={1}>Single Round-Robin (1 match per pairing)</option>
                      <option value={2}>Double Round-Robin (2 matches - Home & Away)</option>
                      <option value={3}>Triple Round-Robin (3 matches per pairing)</option>
                    </select>
                    <p className={`text-xs ${theme.textMuted} mt-1`}>
                      {selectedFormat.id === 'ipl_style'
                        ? `With ${selectedParticipantsForSchedule.length} teams: ${(selectedParticipantsForSchedule.length * (selectedParticipantsForSchedule.length - 1) / 2) * formatOptions.matchesPerPairing} league matches (each team plays ${(selectedParticipantsForSchedule.length - 1) * formatOptions.matchesPerPairing} matches)`
                        : 'How many times each pair of teams/players face each other'}
                    </p>
                  </div>
                )}

                {/* Teams to qualify (for playoff formats) */}
                {['league_plus_finals', 'ipl_style'].includes(selectedFormat.id) && (
                  <div>
                    <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                      Teams Qualifying for Playoffs
                    </label>
                    <select
                      value={formatOptions.teamsToQualify}
                      onChange={(e) => setFormatOptions(prev => ({
                        ...prev,
                        teamsToQualify: parseInt(e.target.value)
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                    >
                      <option value={2}>Top 2</option>
                      <option value={4}>Top 4</option>
                      <option value={6}>Top 6</option>
                      <option value={8}>Top 8</option>
                    </select>
                  </div>
                )}

                {/* Groups + advancement (Pool Play + Bracket) */}
                {selectedFormat.id === 'group_knockout' && (
                  <>
                    <div>
                      <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                        Number of Groups
                      </label>
                      <input
                        type="number"
                        min={2}
                        value={formatOptions.numberOfGroups}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormatOptions(prev => ({
                            ...prev,
                            numberOfGroups: val === '' ? '' : parseInt(val)
                          }));
                        }}
                        onBlur={(e) => {
                          if (e.target.value === '' || parseInt(e.target.value) < 2) {
                            setFormatOptions(prev => ({ ...prev, numberOfGroups: 2 }));
                          }
                        }}
                        className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                      />
                      <p className={`text-xs ${theme.textMuted} mt-1`}>
                        Teams are split evenly into this many pools for round-robin play
                      </p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                        Teams Advancing per Group
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={formatOptions.advancePerGroup}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormatOptions(prev => ({
                            ...prev,
                            advancePerGroup: val === '' ? '' : parseInt(val)
                          }));
                        }}
                        onBlur={(e) => {
                          if (e.target.value === '' || parseInt(e.target.value) < 1) {
                            setFormatOptions(prev => ({ ...prev, advancePerGroup: 1 }));
                          }
                        }}
                        className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                      />
                      <p className={`text-xs ${theme.textMuted} mt-1`}>
                        Top finishers move on to the bracket — seeding is based on group wins, not the standings rank shown on the Standings tab
                      </p>
                    </div>
                  </>
                )}

                {/* Custom Format / MLP inputs — MLP reuses the same panel,
                    just without the Matchup Style choice (gated separately
                    below since MLP is always parallel). */}
                {isCustomLikeFormat && (
                  <>
                    {/* MLP rosters are always exactly 4 (2 doubles pairs) —
                        not a per-tournament choice, so this is fixed rather
                        than editable. Custom Format no longer exposes this
                        at all — it's a simple rounds/games-per-round/playoffs
                        tool now, not a per-team roster/combination builder. */}
                    {isMLP && (
                      <div>
                        <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                          Players per Team
                        </label>
                        <div className={`w-full px-3 py-2 border rounded-lg ${theme.input} opacity-75`}>4</div>
                        <p className={`text-xs ${theme.textMuted} mt-1`}>Fixed for MLP — 2 doubles pairs per team</p>
                      </div>
                    )}
                    {/* Custom Format is now a simple tool — the organizer
                        directly provides Number of Rounds/Games per
                        Round/Playoff Games and that's exactly what gets
                        generated, no smart defaults or auto-recompute. */}
                    {!isMLP && (
                      <>
                        <div>
                          <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                            Number of Rounds
                          </label>
                          <input
                            type="number"
                            min={1}
                            value={formatOptions.numberOfRounds}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormatOptions(prev => ({
                                ...prev,
                                numberOfRounds: val === '' ? '' : parseInt(val)
                              }));
                            }}
                            onBlur={(e) => {
                              if (e.target.value === '' || parseInt(e.target.value) < 1) {
                                setFormatOptions(prev => ({ ...prev, numberOfRounds: 1 }));
                              }
                            }}
                            className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                            Games per Round
                          </label>
                          <input
                            type="number"
                            min={1}
                            value={formatOptions.gamesPerRound}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormatOptions(prev => ({
                                ...prev,
                                gamesPerRound: val === '' ? '' : parseInt(val)
                              }));
                            }}
                            onBlur={(e) => {
                              if (e.target.value === '' || parseInt(e.target.value) < 1) {
                                setFormatOptions(prev => ({ ...prev, gamesPerRound: 1 }));
                              }
                            }}
                            className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                          />
                          <p className={`text-xs ${theme.textMuted} mt-1`}>
                            How many team-vs-team matches happen within each round
                          </p>
                        </div>
                      </>
                    )}
                    {/* MLP: Tournament Structure picks between the 2
                        supported multi-team structures — both require
                        groups to be defined, so grouping isn't an optional
                        checkbox here like Custom Format, it's shown
                        unconditionally once there are 3+ teams. */}
                    {isMLP && selectedParticipantsForSchedule.length > 2 && (
                      <div className="sm:col-span-2">
                        <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                          Tournament Structure
                        </label>
                        <select
                          value={formatOptions.mlpStructure}
                          onChange={(e) => setFormatOptions(prev => ({ ...prev, mlpStructure: e.target.value }))}
                          className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                        >
                          <option value="cross_group">Cross-group (Dual Match) — groups only face each other</option>
                          <option value="group_playoffs">Group Stage + Playoffs — round-robin within each group, then playoffs</option>
                        </select>
                        <p className={`text-xs ${theme.textMuted} mt-1`}>
                          {formatOptions.mlpStructure === 'cross_group'
                            ? 'Each group only faces the other group(s), never their own — standings decide the winner, no playoff round'
                            : 'Teams play round-robin within their own group, then top finishers advance to a Playoffs round'}
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                              Number of Groups
                            </label>
                            <input
                              type="number"
                              min={2}
                              value={formatOptions.numberOfGroups}
                              onChange={(e) => {
                                const val = e.target.value;
                                setFormatOptions(prev => ({
                                  ...prev,
                                  numberOfGroups: val === '' ? '' : parseInt(val)
                                }));
                              }}
                              onBlur={(e) => {
                                if (e.target.value === '' || parseInt(e.target.value) < 2) {
                                  setFormatOptions(prev => ({ ...prev, numberOfGroups: 2 }));
                                }
                              }}
                              className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                            />
                          </div>
                          {formatOptions.mlpStructure === 'group_playoffs' && (
                            <div>
                              <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                                Number of Playoff Games
                              </label>
                              <input
                                type="number"
                                min={0}
                                value={formatOptions.numberOfPlayoffGames}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setFormatOptions(prev => ({
                                    ...prev,
                                    numberOfPlayoffGames: val === '' ? '' : parseInt(val)
                                  }));
                                }}
                                onBlur={(e) => {
                                  if (e.target.value === '' || parseInt(e.target.value) < 0) {
                                    setFormatOptions(prev => ({ ...prev, numberOfPlayoffGames: 0 }));
                                  }
                                }}
                                className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                              />
                              <p className={`text-xs ${theme.textMuted} mt-1`}>
                                Teams TBD — resolved from standings once group play finishes
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="mt-4">
                          <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                            Group Names (optional)
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {Array.from({ length: Number(formatOptions.numberOfGroups) || 2 }, (_, g) => (
                              <input
                                key={g}
                                type="text"
                                placeholder={`Group ${g + 1}`}
                                value={formatOptions.groupNames?.[g] || ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setFormatOptions(prev => {
                                    const groupNames = { ...prev.groupNames };
                                    if (val === '') delete groupNames[g];
                                    else groupNames[g] = val;
                                    return { ...prev, groupNames };
                                  });
                                }}
                                className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                              />
                            ))}
                          </div>
                        </div>

                        <div className="mt-4">
                          <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                            Assign Teams to Groups
                          </label>
                          <p className={`text-xs ${theme.textMuted} mb-3`}>
                            Every team needs a group — teams start out balanced evenly; change any of them below.
                          </p>
                          <div className="space-y-2">
                            {selectedParticipantsForSchedule.map((p, idx) => {
                              const numGroups = Number(formatOptions.numberOfGroups) || 2;
                              const assignedGroup = formatOptions.groupAssignments?.[p.id] ?? (idx % numGroups);
                              return (
                                <div key={p.id} className="flex items-center justify-between gap-3">
                                  <span className={`text-sm ${theme.text} truncate`}>{p.name}</span>
                                  <select
                                    value={assignedGroup}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setFormatOptions(prev => ({
                                        ...prev,
                                        groupAssignments: { ...prev.groupAssignments, [p.id]: parseInt(val) }
                                      }));
                                    }}
                                    className={`px-3 py-1.5 border rounded-lg text-sm ${theme.input}`}
                                  >
                                    {Array.from({ length: numGroups }, (_, g) => (
                                      <option key={g} value={g}>{formatOptions.groupNames?.[g] || `Group ${g + 1}`}</option>
                                    ))}
                                  </select>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* MLP is a fixed league format — no playoff stage to
                        configure, unless Tournament Structure is Group Stage
                        + Playoffs (that field is shown above instead, next
                        to Number of Groups). */}
                    {!isMLP && (
                      <div>
                        <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                          Number of Playoff Games
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={formatOptions.numberOfPlayoffGames}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFormatOptions(prev => ({
                              ...prev,
                              numberOfPlayoffGames: val === '' ? '' : parseInt(val)
                            }));
                          }}
                          onBlur={(e) => {
                            if (e.target.value === '' || parseInt(e.target.value) < 0) {
                              setFormatOptions(prev => ({ ...prev, numberOfPlayoffGames: 0 }));
                            }
                          }}
                          className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                        />
                        <p className={`text-xs ${theme.textMuted} mt-1`}>
                          Teams TBD — resolved from standings once the league rounds finish
                        </p>
                      </div>
                    )}
                  </>
                )}

              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setScheduleStep('select')}
              className={`px-6 py-2 border ${theme.border} ${theme.text} rounded-lg ${theme.cardHover} transition-all flex items-center`}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous: Select Teams
            </button>
            <button
              onClick={async () => {
                // MLP with 3+ teams always uses groups (see mlpScheduler.js
                // comment on useGrouping) — resolve the final assignment
                // (manual picks + auto-distributed "Random" teams, same
                // logic the scheduler itself uses) and save it right here,
                // so group membership is durable even before a schedule is
                // generated/saved.
                if (isMLP && selectedParticipantsForSchedule.length > 2) {
                  const numGroups = Number(formatOptions.numberOfGroups) || 2;
                  const assignment = assignGroups(selectedParticipantsForSchedule, numGroups, formatOptions.groupAssignments);
                  const groupsPayload = Array.from({ length: numGroups }, (_, g) => ({
                    groupName: formatOptions.groupNames?.[g] || `Group ${g + 1}`,
                    teamIds: selectedParticipantsForSchedule
                      .filter((_, idx) => assignment[idx] === g)
                      .map(p => p.id)
                  })).filter(group => group.teamIds.length > 0);

                  if (groupsPayload.length > 0) {
                    setSavingGroups(true);
                    try {
                      await tournamentTeamGroupsAPI.setTeamGroups(tournament.id, groupsPayload);
                    } catch (error) {
                      console.error('Failed to save team groups:', error);
                      toast.error(error.message || 'Failed to save group assignments');
                      setSavingGroups(false);
                      return;
                    }
                    setSavingGroups(false);
                  }
                }
                setScheduleStep('preview');
              }}
              disabled={!selectedFormat || savingGroups}
              className={`px-6 py-2 ${theme.gradient} text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center`}
            >
              {savingGroups ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving groups...
                </>
              ) : (
                <>
                  Next: Preview
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </button>
          </div>
        </div>
          )}
        </>
      )}

      {/* Step 3: Preview & Generate */}
      {scheduleStep === 'preview' && !generatedSchedule && (
        <div className={`${theme.backgroundSecondary} rounded-lg p-6`}>
          <div className="flex items-center justify-between mb-6">
            <h4 className={`font-semibold ${theme.text}`}>
              Preview & Generate Schedule
            </h4>
            <div className={`text-sm ${theme.textSecondary}`}>
              {selectedFormat?.name}
            </div>
          </div>

          {/* Summary Card */}
          <div className={`${theme.card} rounded-lg p-4 border ${theme.border} mb-6`}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <div className={`text-xs ${theme.textMuted} uppercase`}>Format</div>
                <div className={`font-semibold ${theme.text}`}>{selectedFormat?.name}</div>
              </div>
              <div>
                <div className={`text-xs ${theme.textMuted} uppercase`}>Participants</div>
                <div className={`font-semibold ${theme.text}`}>{selectedParticipantsForSchedule.length}</div>
              </div>
              <div>
                <div className={`text-xs ${theme.textMuted} uppercase`}>Type</div>
                <div className={`font-semibold ${theme.text} capitalize`}>{tournament?.participantsType || 'Team'}</div>
              </div>
              {isMLP ? (
                <div>
                  <div className={`text-xs ${theme.textMuted} uppercase`}>Players per Team</div>
                  <div className={`font-semibold ${theme.text}`}>{formatOptions.playersPerTeam}</div>
                </div>
              ) : (
                <div>
                  <div className={`text-xs ${theme.textMuted} uppercase`}>Seeding</div>
                  <div className={`font-semibold ${theme.text}`}>Random</div>
                </div>
              )}
            </div>

            {/* Custom Format's/MLP's own inputs don't show up anywhere else
                on this screen, so they're echoed back here for a final
                sanity check before generating. */}
            {isCustomLikeFormat && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div>
                  <div className={`text-xs ${theme.textMuted} uppercase`}>Rounds</div>
                  <div className={`font-semibold ${theme.text}`}>{formatOptions.numberOfRounds}</div>
                </div>
                <div>
                  <div className={`text-xs ${theme.textMuted} uppercase`}>Games per Round</div>
                  <div className={`font-semibold ${theme.text}`}>{formatOptions.gamesPerRound}</div>
                </div>
                {isMLP && (
                  <div>
                    <div className={`text-xs ${theme.textMuted} uppercase`}>Max Games vs. Same Opponent</div>
                    <div className={`font-semibold ${theme.text}`}>{formatOptions.gamesPerTeam}</div>
                  </div>
                )}
                <div>
                  <div className={`text-xs ${theme.textMuted} uppercase`}>Total League Matches</div>
                  <div className={`font-semibold ${theme.text}`}>
                    {liveScheduleCalc?.totalMatches ?? (formatOptions.numberOfRounds * formatOptions.gamesPerRound)}
                  </div>
                </div>
                <div>
                  <div className={`text-xs ${theme.textMuted} uppercase`}>Total Playoff Matches</div>
                  <div className={`font-semibold ${theme.text}`}>{formatOptions.numberOfPlayoffGames || 0}</div>
                </div>
                {isMLP && selectedParticipantsForSchedule.length > 2 && (
                  <>
                    <div>
                      <div className={`text-xs ${theme.textMuted} uppercase`}>Groups</div>
                      <div className={`font-semibold ${theme.text}`}>{formatOptions.numberOfGroups}</div>
                    </div>
                    <div>
                      <div className={`text-xs ${theme.textMuted} uppercase`}>Structure</div>
                      <div className={`font-semibold ${theme.text}`}>
                        {formatOptions.mlpStructure === 'group_playoffs' ? 'Group Stage + Playoffs' : 'Cross-group (Dual Match)'}
                      </div>
                    </div>
                  </>
                )}
                {!isMLP && formatOptions.useGrouping && (
                  <>
                    <div>
                      <div className={`text-xs ${theme.textMuted} uppercase`}>Groups</div>
                      <div className={`font-semibold ${theme.text}`}>{formatOptions.numberOfGroups}</div>
                    </div>
                    <div>
                      <div className={`text-xs ${theme.textMuted} uppercase`}>Teams Play</div>
                      <div className={`font-semibold ${theme.text}`}>
                        {formatOptions.groupMode === 'within' ? 'Within their own group' : 'Across other groups'}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Participants Preview */}
          <div className={`${theme.card} rounded-lg p-4 border ${theme.border} mb-6`}>
            <h5 className={`font-medium ${theme.text} mb-3`}>Participating {tournament?.participantsType === 'team' ? 'Teams' : 'Players'}</h5>
            <div className="flex flex-wrap gap-2">
              {selectedParticipantsForSchedule.map((p) => (
                <div key={p.id} className={`px-3 py-1.5 rounded-full text-sm ${theme.backgroundSecondary} ${theme.text} border ${theme.border}`}>
                  {p.name}
                </div>
              ))}
            </div>
          </div>

          {/* Info Note */}
          <div className={`p-3 rounded-lg ${theme.card} border ${theme.border} mb-6`}>
            <div className="flex items-start gap-x-2">
              <AlertCircle className={`w-4 h-4 mt-0.5 ${theme.textMuted}`} />
              <div className={`text-sm ${theme.textMuted}`}>
                The schedule will be randomly generated based on your selected format.
                You can regenerate for different matchups or manually adjust matches after generation.
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setScheduleStep('configure')}
              className={`px-6 py-2 border ${theme.border} ${theme.text} rounded-lg ${theme.cardHover} transition-all flex items-center`}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous: Format
            </button>
            <button
              onClick={async () => {
                if (!selectedFormat) {
                  toast.error('Please select a tournament format first');
                  console.error('[ScheduleTab] selectedFormat is null/undefined');
                  setScheduleStep('configure');
                  return;
                }

                setScheduleGenerating(true);
                try {
                  let schedule;
                  if (isMLP) {
                    // MLP builds its fixed 5-game-per-pairing slate directly
                    // from rosters — not the generic engine, and not a
                    // separate assignPlayersToRounds post-process.
                    const rosterByTeamId = await buildRosterByTeamIdWithGenderOverrides(selectedParticipantsForSchedule);
                    schedule = generateMLPSchedule(selectedParticipantsForSchedule, rosterByTeamId, {
                      useGrouping: selectedParticipantsForSchedule.length > 2,
                      numberOfGroups: formatOptions.numberOfGroups,
                      groupAssignments: formatOptions.groupAssignments,
                      structure: formatOptions.mlpStructure,
                      numberOfPlayoffGames: formatOptions.numberOfPlayoffGames,
                    });
                  } else if (selectedFormat.id === 'custom') {
                    schedule = generateCustomFormat(
                      selectedParticipantsForSchedule,
                      { ...formatOptions, requiredPlayerCount }
                    );
                    if (needsPlayerPicker) {
                      const rosterByTeamId = await buildRosterByTeamId(selectedParticipantsForSchedule);
                      schedule = { ...schedule, rounds: assignPlayersToRounds(schedule.rounds, rosterByTeamId, requiredPlayerCount) };
                    }
                  } else if (isCompassDraw) {
                    schedule = generateCompassDraw(selectedParticipantsForSchedule);
                  } else {
                    schedule = generateTournamentSchedule(
                      selectedParticipantsForSchedule,
                      selectedFormat.id,
                      { ...formatOptions }
                    );
                  }
                  setGeneratedSchedule(schedule);
                  setScheduleStep('generated');
                  toast.success(`${selectedFormat.name} schedule generated!`);
                  // Stable id (content-based) so generating again with the
                  // same structural warning just refreshes the existing
                  // toast instead of stacking a new identical one each time.
                  schedule.warnings?.forEach(w => toast(w, { id: `schedule-warning-${w}`, icon: '⚠️', duration: 6000 }));
                } catch (error) {
                  console.error('Schedule generation error:', error);
                  toast.error(error.message || 'Failed to generate schedule');
                } finally {
                  setScheduleGenerating(false);
                }
              }}
              disabled={scheduleGenerating}
              className={`px-6 py-2 ${theme.gradient} text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 flex items-center`}
            >
              {scheduleGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Shuffle className="w-4 h-4 mr-2" />
                  Generate Schedule
                </>
              )}
            </button>
          </div>

        </div>
      )}

      {/* Generated Schedule Display */}
      {generatedSchedule && (
        <div className="space-y-4">
          {/* Schedule Summary */}
          <div className={`${theme.backgroundSecondary} rounded-lg p-4`}>
            <div className="flex flex-wrap items-center gap-4">
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${theme.gradient} text-white`}>
                {generatedSchedule.formatName}
              </div>
              <div className={`text-sm ${theme.textSecondary}`}>
                {generatedSchedule.participants?.length} participants
              </div>
              <div className={`text-sm ${theme.textSecondary}`}>
                {generatedSchedule.matches?.length} matches
              </div>
              <div className={`text-sm ${theme.textSecondary}`}>
                {generatedSchedule.rounds?.length} rounds
              </div>
              {generatedSchedule.byesNeeded > 0 && (
                <div className={`text-sm text-yellow-600`}>
                  {generatedSchedule.byesNeeded} bye(s)
                </div>
              )}
            </div>
          </div>

          {/* Matches Table */}
          <div className="rounded-lg overflow-hidden border border-gray-400 dark:border-gray-500">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse min-w-[500px]">
                <thead>
                  <tr className={`border-b-2 border-gray-400 dark:border-gray-500`}>
                    <th className={`px-4 py-3 text-left text-xs font-semibold ${theme.textMuted} w-12 border-r border-gray-400 dark:border-gray-500`}>#</th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold ${theme.textMuted} border-r border-gray-400 dark:border-gray-500`}>Round</th>
                    {isMLP && (
                      <th className={`px-4 py-3 text-left text-xs font-semibold ${theme.textMuted} border-r border-gray-400 dark:border-gray-500`}>Game</th>
                    )}
                    <th className={`px-4 py-3 text-left text-xs font-semibold ${theme.textMuted} border-r border-gray-400 dark:border-gray-500`}>Home Team</th>
                    <th className={`px-4 py-3 text-center text-xs font-semibold ${theme.textMuted} w-12 border-r border-gray-400 dark:border-gray-500`}>vs</th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold ${theme.textMuted}`}>Away Team</th>
                  </tr>
                </thead>
                <tbody>
                  {generatedSchedule.rounds?.flatMap((round, roundIdx) =>
                    round.matches?.length > 0
                      ? round.matches.map((match, matchIdx) => (
                          <tr key={match.id} className={`border-b border-gray-300 dark:border-gray-600 last:border-0 ${(roundIdx + matchIdx) % 2 === 0 ? 'bg-black/5 dark:bg-white/5' : ''}`}>
                            <td className={`px-4 py-2.5 text-xs ${theme.textMuted} border-r border-gray-300 dark:border-gray-600`}>{match.matchNumber}</td>
                            <td className={`px-4 py-2.5 border-r border-gray-300 dark:border-gray-600`}>
                              <span className={`text-xs font-medium ${theme.text}`}>{round.roundName}</span>
                              {round.bracket && (
                                <span className={`ml-1.5 text-xs ${theme.textMuted}`}>({round.bracket})</span>
                              )}
                            </td>
                            {isMLP && (
                              <td className={`px-4 py-2.5 text-xs ${theme.textMuted} border-r border-gray-300 dark:border-gray-600`}>
                                {match.gameTypeLabel || '—'}
                              </td>
                            )}
                            <td className={`px-4 py-2.5 font-medium border-r border-gray-300 dark:border-gray-600 ${match.teamA?.isBye ? 'italic text-gray-400' : theme.text}`}>
                              {match.teamA?.name || 'TBD'}
                              {match.participantAPlayers?.length > 0 && (
                                <div className={`text-xs font-normal ${theme.textMuted}`}>
                                  {match.participantAPlayers.map(p => p.name).join(' / ')}
                                </div>
                              )}
                            </td>
                            <td className={`px-4 py-2.5 text-center text-xs ${theme.textMuted} border-r border-gray-300 dark:border-gray-600`}>vs</td>
                            <td className={`px-4 py-2.5 font-medium ${match.teamB?.isBye ? 'italic text-gray-400' : theme.text}`}>
                              {match.teamB?.name || (match.status === 'bye' ? 'BYE' : 'TBD')}
                              {match.participantBPlayers?.length > 0 && (
                                <div className={`text-xs font-normal ${theme.textMuted}`}>
                                  {match.participantBPlayers.map(p => p.name).join(' / ')}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      : [
                          <tr key={`tbd-${round.roundNumber}`} className={`border-b border-gray-300 dark:border-gray-600 last:border-0`}>
                            <td className={`px-4 py-2.5 text-xs ${theme.textMuted} border-r border-gray-300 dark:border-gray-600`}>—</td>
                            <td className={`px-4 py-2.5 text-xs font-medium ${theme.text} border-r border-gray-300 dark:border-gray-600`}>{round.roundName}</td>
                            <td colSpan={isMLP ? 4 : 3} className={`px-4 py-2.5 text-xs ${theme.textMuted} italic`}>
                              Determined by previous round results
                            </td>
                          </tr>
                        ]
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* IPL-Style Playoff Bracket Visualization */}
          {generatedSchedule.format === 'ipl_style' && generatedSchedule.playoffStructure && (
            <div className={`${theme.backgroundSecondary} rounded-lg p-6`}>
              <h4 className={`font-semibold ${theme.text} mb-4 flex items-center`}>
                <Zap className="w-5 h-5 mr-2 text-yellow-500" />
                Playoff Bracket
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                {/* Qualifier 1 */}
                <div className={`${theme.card} rounded-lg p-3 border-2 border-blue-500`}>
                  <div className={`text-xs font-medium text-blue-500 mb-2`}>QUALIFIER 1</div>
                  <div className={`${theme.text} text-sm`}>1st vs 2nd</div>
                  <div className={`text-xs ${theme.textMuted} mt-1`}>Winner → Final</div>
                </div>

                {/* Eliminator */}
                <div className={`${theme.card} rounded-lg p-3 border-2 border-red-500`}>
                  <div className={`text-xs font-medium text-red-500 mb-2`}>ELIMINATOR</div>
                  <div className={`${theme.text} text-sm`}>3rd vs 4th</div>
                  <div className={`text-xs ${theme.textMuted} mt-1`}>Loser eliminated</div>
                </div>

                {/* Qualifier 2 */}
                <div className={`${theme.card} rounded-lg p-3 border-2 border-purple-500`}>
                  <div className={`text-xs font-medium text-purple-500 mb-2`}>QUALIFIER 2</div>
                  <div className={`${theme.text} text-sm`}>Q1 Loser vs Elim Winner</div>
                  <div className={`text-xs ${theme.textMuted} mt-1`}>Winner → Final</div>
                </div>

                {/* Final */}
                <div className={`${theme.card} rounded-lg p-3 border-2 border-yellow-500`}>
                  <div className={`text-xs font-medium text-yellow-600 mb-2`}>FINAL</div>
                  <div className={`${theme.text} text-sm`}>Q1 Winner vs Q2 Winner</div>
                  <div className={`text-xs ${theme.textMuted} mt-1`}>Championship</div>
                </div>
              </div>
            </div>
          )}

          {/* Compass Draw Bracket Visualization */}
          {generatedSchedule.format === 'compass_draw' && (
            <CompassBracketView schedule={generatedSchedule} />
          )}

          {/* MLP identity notice — genders are randomly assigned until set
              here, since real profile gender isn't wired through yet. */}
          {isMLP && (
            <div className={`p-3 rounded-lg border ${theme.border} ${theme.backgroundSecondary} flex flex-wrap items-center justify-between gap-3`}>
              <p className="text-sm text-red-500">
                We don't have full player identity yet, so Men's/Women's/Mixed assignments above were generated randomly.
                If you want more accurate assignments, set player identities below and regenerate.
              </p>
              <button
                type="button"
                onClick={handleOpenMlpIdentityModal}
                className="px-3 py-1.5 text-sm rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-medium shadow-sm whitespace-nowrap"
              >
                Set Player Identity
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className={`${theme.backgroundSecondary} rounded-lg p-4`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <button
                onClick={() => {
                  setGeneratedSchedule(null);
                  setScheduleStep('preview');
                }}
                className={`px-4 py-2 border ${theme.border} ${theme.text} rounded-lg ${theme.cardHover} flex items-center`}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Preview
              </button>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={regenerateSchedule}
                  className={`px-4 py-2 border ${theme.border} ${theme.text} rounded-lg ${theme.cardHover} flex items-center`}
                >
                  <Shuffle className="w-4 h-4 mr-2" />
                  Regenerate
                </button>
                <button
                  onClick={async () => {
                    if (!tournament?.id) {
                      toast.error('Tournament ID is missing');
                      return;
                    }

                    if (!selectedFormat) {
                      toast.error('Please select a tournament format first');
                      console.error('[ScheduleTab] selectedFormat is null/undefined');
                      return;
                    }


                    // Check if schedule already exists
                    if (apiRounds.length > 0) {
                      const confirmed = window.confirm(
                        'A schedule already exists for this tournament.\n\n' +
                        'Saving will override the existing schedule and delete all current matches.\n\n' +
                        'Do you want to continue?'
                      );

                      if (!confirmed) {
                        // User cancelled - go back to existing schedule
                        setGeneratedSchedule(null);
                        setScheduleStep('select');
                        toast('Cancelled - keeping existing schedule');
                        return;
                      }

                      // User confirmed - delete existing schedule first
                      try {
                        await tournamentAPI.deleteSchedule(tournament.id);
                        toast.success('Existing schedule deleted');
                      } catch (error) {
                        console.error('Failed to delete existing schedule:', error);
                        toast.error('Failed to delete existing schedule');
                        return;
                      }
                    }

                    setGeneratingSchedule(true);
                    try {
                      // Map frontend format to backend format — MLP reuses
                      // Custom Format's blind-save endpoint verbatim (same
                      // generation engine, same persisted shape), so the
                      // backend only ever needs to know about 'custom'.
                      let backendFormat = isMLP ? 'custom' : selectedFormat.id;
                      let config = {};


                      // For IPL style, use user's choice of matches per pairing
                      if (selectedFormat.id === 'ipl_style') {
                        config = {
                          matchesPerPairing: formatOptions.matchesPerPairing || 2,
                          teamsToQualify: formatOptions.teamsToQualify || 4
                        };
                      }
                      // For league plus finals
                      else if (selectedFormat.id === 'league_plus_finals') {
                        config = {
                          matchesPerPairing: formatOptions.matchesPerPairing || 1,
                          teamsToQualify: formatOptions.teamsToQualify || 4
                        };
                      }
                      // Group Play + Bracket — backend does the actual pool
                      // draw and bracket seeding server-side (populateKnockoutFromGroups
                      // fills the bracket once each pool's games complete), so
                      // this is the full config it needs, not a computed schedule.
                      else if (selectedFormat.id === 'group_knockout') {
                        config = {
                          groups: formatOptions.numberOfGroups || 2,
                          advancePerGroup: formatOptions.advancePerGroup || 2
                        };
                      }
                      // Single elimination: third-place playoff + consolation bracket toggles
                      else if (selectedFormat.id === 'single_elimination') {
                        config = {
                          thirdPlace: !!formatOptions.thirdPlace,
                          consolation: formatOptions.consolation || 'none',
                        };
                      }
                      // Custom Format / MLP: backend does no generation of its own — it
                      // blindly persists the exact rounds/matches already computed and
                      // shown in this preview, so the saved schedule always matches what
                      // was approved.
                      else if (isCustomLikeFormat) {
                        if (!generatedSchedule) {
                          toast.error('Generate a preview before saving');
                          setGeneratingSchedule(false);
                          return;
                        }
                        config = {
                          scheduleData: { rounds: generatedSchedule.rounds }
                        };
                      }


                      const result = await tournamentAPI.generateSchedule(tournament.id, {
                        format: backendFormat,
                        config
                      });

                      // Group membership itself (e.g. MLP cross-group clusters) is
                      // already saved earlier, on the Format Options step (see the
                      // "Next: Preview" handler) — nothing to persist here.

                      toast.success(`Schedule saved: ${result.rounds} rounds, ${result.matches} matches`);

                      // Reload schedule from API
                      const rounds = await tournamentScheduleAPI.getRounds(tournament.id);
                      setApiRounds(rounds || []);

                      // Clear local preview and show saved schedule
                      setGeneratedSchedule(null);
                      setScheduleStep('select');
                    } catch (error) {
                      console.error('Failed to save schedule:', error);
                      toast.error(error.message || 'Failed to save schedule');
                    } finally {
                      setGeneratingSchedule(false);
                    }
                  }}
                  disabled={generatingSchedule}
                  className={`px-4 py-2 ${theme.gradient} text-white rounded-lg hover:shadow-lg flex items-center ${generatingSchedule ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {generatingSchedule ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save Schedule
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Match Modal */}
      <AddEventModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingMatch(null);
        }}
        initialData={editingMatch?.initialData}
        onSave={handleSaveMatch}
        googleMapsApiKey={getConfig('GOOGLE_PLACES_API_KEY')}
      />

      {/* Add Round Modal */}
      {showAddRoundModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className={`${theme.card} rounded-lg max-w-md w-full p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-bold ${theme.text}`}>Add New Round</h3>
              <button
                onClick={() => {
                  setShowAddRoundModal(false);
                  setNewRoundData({ name: '', type: 'round_robin' });
                }}
                className={`p-1 rounded-lg ${theme.cardHover}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Round Name *
                </label>
                <input
                  type="text"
                  value={newRoundData.name}
                  onChange={(e) => setNewRoundData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Quarter Finals, Group Stage"
                  className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Round Type *
                </label>
                <select
                  value={newRoundData.type}
                  onChange={(e) => setNewRoundData(prev => ({ ...prev, type: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                >
                  {/* Values must match what the League Matches (round_robin) and
                      Playoff Matches (qualifier/eliminator/knockout) sections
                      filter on below — any other value renders nowhere. */}
                  <option value="round_robin">League</option>
                  <option value="knockout">Playoff / Knockout</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowAddRoundModal(false);
                  setNewRoundData({ name: '', type: 'round_robin' });
                }}
                className={`px-4 py-2 border ${theme.border} ${theme.text} rounded-lg ${theme.cardHover}`}
              >
                Cancel
              </button>
              <button
                onClick={handleAddRound}
                disabled={!newRoundData.name || !newRoundData.type}
                className={`px-4 py-2 ${theme.gradient} text-white rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Create Round
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Match Modal */}
      {showAddMatchModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className={`${theme.card} rounded-lg max-w-md w-full p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-bold ${theme.text}`}>Add New Match</h3>
              <button
                onClick={() => {
                  setShowAddMatchModal(false);
                  setSelectedRoundForNewMatch(null);
                  setNewMatchData({ participantA: null, participantB: null });
                }}
                className={`p-1 rounded-lg ${theme.cardHover}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Participant A *
                </label>
                <select
                  value={newMatchData.participantA || ''}
                  onChange={(e) => setNewMatchData(prev => ({ ...prev, participantA: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                >
                  <option value="">Select {tournament?.participantsType || 'Team'}</option>
                  {participants
                    .filter(p => p.teamId || (p.participantId && p.player))
                    .filter(p => `${p.teamId ? 'team' : 'user'}:${p.teamId || p.participantId}` !== newMatchData.participantB)
                    .map(p => {
                      const id = p.teamId || p.participantId;
                      const type = p.teamId ? 'team' : 'user';
                      const name = p.teamName || p.team?.teamName || p.participantName || `Player ${p.participantId}`;
                      return (
                        <option key={`${type}:${id}`} value={`${type}:${id}`}>
                          {name}
                        </option>
                      );
                    })}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Participant B *
                </label>
                <select
                  value={newMatchData.participantB || ''}
                  onChange={(e) => setNewMatchData(prev => ({ ...prev, participantB: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                >
                  <option value="">Select {tournament?.participantsType || 'Team'}</option>
                  {participants
                    .filter(p => p.teamId || (p.participantId && p.player))
                    .filter(p => `${p.teamId ? 'team' : 'user'}:${p.teamId || p.participantId}` !== newMatchData.participantA)
                    .map(p => {
                      const id = p.teamId || p.participantId;
                      const type = p.teamId ? 'team' : 'user';
                      const name = p.teamName || p.team?.teamName || p.participantName || `Player ${p.participantId}`;
                      return (
                        <option key={`${type}:${id}`} value={`${type}:${id}`}>
                          {name}
                        </option>
                      );
                    })}
                </select>
              </div>

              {selectedRoundForNewMatch && (
                <div className={`p-3 rounded-lg ${theme.backgroundSecondary}`}>
                  <p className={`text-sm ${theme.textMuted}`}>
                    Adding to Round {selectedRoundForNewMatch}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowAddMatchModal(false);
                  setSelectedRoundForNewMatch(null);
                  setNewMatchData({ participantA: null, participantB: null });
                }}
                className={`px-4 py-2 border ${theme.border} ${theme.text} rounded-lg ${theme.cardHover}`}
              >
                Cancel
              </button>
              <button
                onClick={handleAddMatch}
                disabled={!newMatchData.participantA || !newMatchData.participantB}
                className={`px-4 py-2 ${theme.gradient} text-white rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Create Match
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Start Tournament Confirmation Dialog */}
      {showStartTournamentDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.card} rounded-lg max-w-md w-full p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-bold ${theme.text} flex items-center gap-x-2`}>
                <Play className="w-6 h-6 text-blue-500" />
                <span>Start Tournament</span>
              </h3>
              <button
                onClick={() => setShowStartTournamentDialog(false)}
                disabled={startingTournament}
                className={`p-1 rounded-lg ${theme.cardHover}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className={`p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700`}>
                <div className="flex items-start gap-x-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className={`text-sm font-medium text-blue-800 dark:text-blue-300`}>
                      This action will start the tournament
                    </p>
                    <p className={`text-xs text-blue-700 dark:text-blue-400 mt-1`}>
                      Once started, the following will be locked:
                    </p>
                    <ul className={`text-xs text-blue-700 dark:text-blue-400 mt-2 space-y-1 list-disc list-inside`}>
                      <li>New participant registrations</li>
                      <li>Participant withdrawals/unenrollment</li>
                      <li>Tournament participant list</li>
                      <li>Tournament format changes</li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className={`text-sm ${theme.textMuted}`}>
                Are you ready to start the tournament? Make sure all participants are registered and the schedule is finalized.
              </p>

              <div className="flex items-center justify-end gap-x-3">
                <button
                  onClick={() => setShowStartTournamentDialog(false)}
                  disabled={startingTournament}
                  className={`px-4 py-2 border ${theme.border} ${theme.text} rounded-lg ${theme.cardHover} disabled:opacity-50`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleStartTournament}
                  disabled={startingTournament}
                  className={`${btn.base} ${btn.md} ${theme.buttonPrimary} disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-x-2`}
                >
                  {startingTournament ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Starting...</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      <span>Start Tournament</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Seed Playoffs Confirmation Dialog */}
      {showSeedPlayoffsDialog && (() => {
        // apiRounds is grouped by round_number (groupGamesByRound) — the actual
        // game status lives on the individual games inside each group, not on
        // the group wrapper itself.
        const alreadyCompletedRounds = apiRounds
          .filter(r => ['qualifier', 'eliminator'].includes(r.round_type) && r.games?.some(g => g.status === 'completed'))
          .map(r => r.round_name);

        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`${theme.card} rounded-lg max-w-md w-full p-6`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-bold ${theme.text} flex items-center gap-x-2`}>
                  <Zap className="w-6 h-6 text-blue-500" />
                  <span>Seed Playoffs</span>
                </h3>
                <button
                  onClick={() => setShowSeedPlayoffsDialog(false)}
                  disabled={seedingPlayoffs}
                  className={`p-1 rounded-lg ${theme.cardHover}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {alreadyCompletedRounds.length > 0 ? (
                  <>
                    {/* Complete denial — no way to proceed while a playoff game is already completed */}
                    <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700">
                      <div className="flex items-start gap-x-2">
                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-800 dark:text-red-300">
                            Cannot seed playoffs
                          </p>
                          <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                            {alreadyCompletedRounds.join(' and ')} already {alreadyCompletedRounds.length > 1 ? 'have' : 'has'} a completed
                            game. Seeding would desync the bracket from a result that's already been recorded, so it's blocked
                            entirely until a new tournament (or a reset of that round) is set up.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-end">
                      <button
                        onClick={() => setShowSeedPlayoffsDialog(false)}
                        className={`px-4 py-2 border ${theme.border} ${theme.text} rounded-lg ${theme.cardHover}`}
                      >
                        Close
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700">
                      <div className="flex items-start gap-x-2">
                        <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                            This will assign teams to Qualifier 1 and Eliminator based on current standings
                          </p>
                          <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                            Any manually-assigned teams in those rounds will be overwritten.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-x-3">
                      <button
                        onClick={() => setShowSeedPlayoffsDialog(false)}
                        disabled={seedingPlayoffs}
                        className={`px-4 py-2 border ${theme.border} ${theme.text} rounded-lg ${theme.cardHover} disabled:opacity-50`}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSeedPlayoffs}
                        disabled={seedingPlayoffs}
                        className={`${btn.base} ${btn.md} ${theme.buttonPrimary} disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-x-2`}
                      >
                        {seedingPlayoffs ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span>Seeding...</span>
                          </>
                        ) : (
                          <>
                            <Zap className="w-4 h-4" />
                            <span>Seed Playoffs</span>
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Complete Tournament Confirmation Dialog */}
      {showCompleteTournamentDialog && (() => {
        const unplayedGamesCount = apiRounds.reduce((count, round) =>
          count + (round.games?.filter(g =>
            g.status !== 'completed' && g.status !== 'Completed' && g.checkin_status !== 'completed'
          ).length || 0), 0);
        return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.card} rounded-lg max-w-md w-full p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-bold ${theme.text} flex items-center gap-x-2`}>
                <Trophy className="w-6 h-6 text-yellow-500" />
                <span>Complete Tournament</span>
              </h3>
              <button
                onClick={() => setShowCompleteTournamentDialog(false)}
                disabled={completingTournament}
                className={`p-1 rounded-lg ${theme.cardHover}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className={`p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700`}>
                <div className="flex items-start gap-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className={`text-sm font-medium text-yellow-800 dark:text-yellow-300`}>
                      This action will finalize the tournament
                    </p>
                    <p className={`text-xs text-yellow-700 dark:text-yellow-400 mt-1`}>
                      Once completed, you won't be able to:
                    </p>
                    <ul className={`text-xs text-yellow-700 dark:text-yellow-400 mt-2 space-y-1 list-disc list-inside`}>
                      <li>Edit match results or scores</li>
                      <li>Add or delete rounds/games</li>
                      <li>Reschedule matches</li>
                      <li>Modify tournament format</li>
                    </ul>
                    {unplayedGamesCount > 0 && (
                      <p className={`text-xs font-medium text-yellow-800 dark:text-yellow-300 mt-2`}>
                        {unplayedGamesCount} game{unplayedGamesCount === 1 ? ' is' : 's are'} still unplayed — {unplayedGamesCount === 1 ? 'it' : 'they'} will be locked as-is and can't be edited after this.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <p className={`text-sm ${theme.textMuted}`}>
                {unplayedGamesCount > 0
                  ? `Are you sure you want to complete the tournament? You won't be able to edit any of the ${unplayedGamesCount} unplayed game${unplayedGamesCount === 1 ? '' : 's'}.`
                  : 'Are you sure all results are correct and you want to complete the tournament?'}
              </p>

              <div className="flex items-center justify-end gap-x-3">
                <button
                  onClick={() => setShowCompleteTournamentDialog(false)}
                  disabled={completingTournament}
                  className={`px-4 py-2 border ${theme.border} ${theme.text} rounded-lg ${theme.cardHover} disabled:opacity-50`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCompleteTournament}
                  disabled={completingTournament}
                  className={`${btn.base} ${btn.md} ${theme.buttonPrimary} disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-x-2`}
                >
                  {completingTournament ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Completing...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Complete Tournament</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Complete Tournament Button — available once a schedule exists,
          regardless of completion status. Some games may legitimately never
          get played (e.g. an MLP Dreambreaker), so this doesn't try to guess
          whether that's "fine" or not — the confirmation dialog just tells
          the user how many games are still unplayed and warns those can't be
          edited afterward. Placed at the very end of the page, after the
          schedule itself.
          Restricted to organizer/co-owner — this was previously shown to any
          viewer regardless of role, and the backend (patchTournament) had no
          authorization check either, so it was a real gap, not just display
          noise. Both are now fixed together (see assertCanManageTournament
          in tournamentsController.js). */}
      {apiRounds.length > 0 && tournament?.status !== 'completed' && (isOrganizer || isCoOwner(user?.id, tournamentVO)) && (
        <div className={`${theme.backgroundSecondary} rounded-lg p-6 border-2 ${theme.border}`}>
          <div className="flex flex-col items-center space-y-4">
            <div className="flex items-center gap-x-2">
              <Trophy className={`w-6 h-6 ${theme.textMuted}`} />
              <h3 className={`text-lg font-semibold ${theme.text}`}>Complete Tournament</h3>
            </div>
            <p className={`text-sm ${theme.textMuted} text-center max-w-md`}>
              Finalize this tournament's results whenever you're ready — completed tournaments (and their matches) become read-only.
            </p>
            <button
              onClick={() => setShowCompleteTournamentDialog(true)}
              className={`${btn.base} ${btn.lg} ${theme.buttonPrimary} flex items-center gap-x-2`}
            >
              <Trophy className="w-5 h-5" />
              <span>Complete Tournament</span>
            </button>
          </div>
        </div>
      )}

      {/* Start Scoring confirmation modal */}
      {pendingStartScoringGame && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className={`${theme.card} rounded-2xl border ${theme.border} p-6 max-w-sm w-full shadow-xl`}>
            <h3 className={`text-lg font-bold ${theme.text} mb-2`}>Start Scoring?</h3>
            <p className={`text-sm ${theme.textMuted} mb-1`}>
              <span className="font-medium">{pendingStartScoringGame.participant_a_name || 'Team A'}</span>
              {' vs '}
              <span className="font-medium">{pendingStartScoringGame.participant_b_name || 'Team B'}</span>
            </p>
            <p className={`text-sm ${theme.textMuted} mb-6`}>
              You will be the sole scorer for this match. Other users will see a read-only view until the game is finished.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setPendingStartScoringGame(null)}
                className={`flex-1 py-2 rounded-xl border ${theme.border} ${theme.text} text-sm font-medium`}
              >
                Cancel
              </button>
              <button
                onClick={() => handleStartScoring(pendingStartScoringGame)}
                className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex-1`}
              >
                Start Scoring
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team roster modal */}
      {teamRosterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setTeamRosterModal(null)}>
          <div className={`w-full max-w-xs rounded-2xl ${theme.card} shadow-xl p-5`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-500" />
                <h3 className={`text-sm font-semibold ${theme.text}`}>{teamRosterModal.teamName}</h3>
              </div>
              <button onClick={() => setTeamRosterModal(null)} className={`p-1 rounded-lg ${theme.textMuted} hover:opacity-70`}>
                <X className="w-4 h-4" />
              </button>
            </div>
            {teamRosterModal.loading ? (
              <div className="flex justify-center py-6">
                <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
              </div>
            ) : teamRosterModal.error ? (
              <p className={`text-sm text-center ${theme.textMuted} py-4`}>Failed to load players</p>
            ) : teamRosterModal.players.length === 0 ? (
              <p className={`text-sm text-center ${theme.textMuted} py-4`}>No players on this team</p>
            ) : (
              <ul className="space-y-1.5">
                {teamRosterModal.players.map((p, i) => (
                  <li key={p.id} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg ${theme.backgroundSecondary}`}>
                    <span className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-white">{i + 1}</span>
                    <span className={`text-sm ${theme.text}`}>{p.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* MLP player identity modal — sets a session-only male/female
          override per player (never saved to their profile) so
          regenerating uses real Men's/Women's splits instead of the
          random fallback. */}
      {mlpIdentityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setMlpIdentityModal(null)}>
          <div className={`w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl ${theme.card} shadow-xl p-5`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-sm font-semibold ${theme.text}`}>Set Player Identity</h3>
              <button onClick={() => setMlpIdentityModal(null)} className={`p-1 rounded-lg ${theme.textMuted} hover:opacity-70`}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className={`text-xs ${theme.textMuted} mb-4`}>
              Not saved to any player's profile — only used to build this schedule. Each team needs exactly 2 Male + 2 Female to enable regenerating.
            </p>

            {mlpIdentityModal.loading ? (
              <div className="flex justify-center py-6">
                <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
              </div>
            ) : mlpIdentityModal.error ? (
              <p className={`text-sm text-center ${theme.textMuted} py-4`}>Failed to load rosters</p>
            ) : (
              <div className="space-y-5">
                {Object.entries(mlpIdentityModal.rosters).map(([teamId, roster]) => {
                  const participant = selectedParticipantsForSchedule.find(p => p.id === teamId);
                  const relevant = (roster || []).slice(0, 4);
                  const males = relevant.filter(p => mlpPlayerGenders[p.id] === 'male').length;
                  const females = relevant.filter(p => mlpPlayerGenders[p.id] === 'female').length;
                  const complete = males === 2 && females === 2;
                  return (
                    <div key={teamId}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${theme.text}`}>{participant?.name || teamId}</span>
                        <span className={`text-xs ${complete ? 'text-green-500' : 'text-amber-500'}`}>
                          {males}M / {females}F{complete ? ' ✓' : ''}
                        </span>
                      </div>
                      <div className="space-y-1.5">
                        {relevant.map(p => (
                          <div key={p.id} className="flex items-center justify-between gap-3">
                            <span className={`text-sm ${theme.text} truncate`}>{p.name}</span>
                            <div className="flex gap-1.5 flex-shrink-0">
                              {['male', 'female'].map(g => (
                                <button
                                  key={g}
                                  type="button"
                                  onClick={() => setMlpPlayerGenders(prev => ({ ...prev, [p.id]: g }))}
                                  className={`px-3 py-1 text-xs rounded-lg border ${theme.border} ${
                                    mlpPlayerGenders[p.id] === g ? `${theme.gradient} text-white border-transparent` : theme.text
                                  }`}
                                >
                                  {g === 'male' ? 'Male' : 'Female'}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!mlpIdentityModal.loading && !mlpIdentityModal.error && (
              <button
                type="button"
                disabled={!Object.values(mlpIdentityModal.rosters).every(mlpIdentityIsCompleteForTeam) || scheduleGenerating}
                onClick={async () => {
                  setMlpIdentityModal(null);
                  await regenerateSchedule();
                }}
                className={`w-full mt-5 px-4 py-2 rounded-lg text-sm font-medium text-white ${theme.gradient} disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                Apply & Regenerate
              </button>
            )}
          </div>
        </div>
      )}

      {/* Per-match settings override modal — lets an organizer set match
          rules (e.g. bestOf, maxPoints) for just this one match, overriding
          the tournament-wide match_configuration when starting scoring for
          it. Off by default; turning the toggle off and saving clears any
          existing override so the match falls back to tournament settings. */}
      {matchSettingsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setMatchSettingsModal(null)}>
          <div className={`w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl ${theme.card} shadow-xl p-5`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-sm font-semibold ${theme.text}`}>Match Settings</h3>
              <button onClick={() => setMatchSettingsModal(null)} className={`p-1 rounded-lg ${theme.textMuted} hover:opacity-70`}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className={`text-xs ${theme.textMuted} mb-4`}>
              Override the tournament's match settings for just this match. When off, this match uses the tournament's own settings.
            </p>

            <label className="flex items-center gap-2 cursor-pointer mb-4">
              <input
                type="checkbox"
                checked={matchSettingsModal.useOverride}
                onChange={(e) => setMatchSettingsModal(prev => ({ ...prev, useOverride: e.target.checked }))}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className={`text-sm ${theme.text}`}>Use custom settings for this match</span>
            </label>

            {matchSettingsModal.useOverride && (
              <MatchSettingsComponent
                sportType={predictionSportName}
                initialSettings={matchSettingsModal.settings}
                // onSettingsChange's payload never includes participantsType
                // (MatchSettingsComponent manages that as a separate
                // controlled prop, not part of its internal settings state)
                // — merge rather than replace so it isn't wiped out here.
                onSettingsChange={(settings) => setMatchSettingsModal(prev => ({ ...prev, settings: { ...prev.settings, ...settings } }))}
                participantsTypeValue={matchSettingsModal.settings.participantsType}
                onParticipantsTypeChange={(participantsType) => setMatchSettingsModal(prev => ({ ...prev, settings: { ...prev.settings, participantsType } }))}
                showNextButton={false}
                compact
              />
            )}

            {/* Team Pairing — which specific players from each already-assigned
                team play this match. Independent of the override toggle above:
                available whenever the sport needs a player subset, whether or
                not this match's rules are customized. */}
            {loadingPairingRosters && (
              <div className={`text-xs ${theme.textMuted} mb-4`}>Loading team rosters...</div>
            )}
            {!loadingPairingRosters && (showPairingA || showPairingB) && (
              <div className="mb-4">
                <h4 className={`text-sm font-semibold ${theme.text} mb-1`}>Team Pairing</h4>
                <p className={`text-xs ${theme.textMuted} mb-2`}>
                  Select which players from each team play this match.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {showPairingA && (
                    <div>
                      <div className={`text-xs font-medium ${theme.text} mb-1 truncate`}>{matchSettingsModal.game.participant_a_name}</div>
                      <div className="space-y-1">
                        {pairingRosterA.map(p => (
                          <label key={p.id} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedPairingPlayersA.some(x => x.id === p.id)}
                              onChange={() => togglePairingPlayer(p, selectedPairingPlayersA, setSelectedPairingPlayersA)}
                              className="w-4 h-4 text-blue-600 rounded"
                            />
                            <span className={`text-sm ${theme.text} truncate`}>{p.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  {showPairingB && (
                    <div>
                      <div className={`text-xs font-medium ${theme.text} mb-1 truncate`}>{matchSettingsModal.game.participant_b_name}</div>
                      <div className="space-y-1">
                        {pairingRosterB.map(p => (
                          <label key={p.id} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedPairingPlayersB.some(x => x.id === p.id)}
                              onChange={() => togglePairingPlayer(p, selectedPairingPlayersB, setSelectedPairingPlayersB)}
                              className="w-4 h-4 text-blue-600 rounded"
                            />
                            <span className={`text-sm ${theme.text} truncate`}>{p.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <button
              type="button"
              disabled={savingMatchSettings}
              onClick={handleSaveMatchSettings}
              className={`w-full mt-5 px-4 py-2 rounded-lg text-sm font-medium text-white ${theme.gradient} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {savingMatchSettings ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
